import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { Ban, ClipboardCheck, Copy, FilePlus2, ListChecks, Mail, MessageSquarePlus, PackageSearch, Pencil, Printer, ReceiptText, RefreshCw, Save, Search, ShoppingBag, WalletCards, X } from "lucide-react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import StoreLayout from "./StoreLayout";
import { preferredStoreLocationId, rememberedStoreLocationId } from "./storeLocationPreference";
import { copyPhoneNumber } from "./phoneActions";
import { hasStorePermission } from "./storePermissions";
import {
  confirmStoreOrderReceipt,
  createStoreOrder,
  createStoreOrderPayment,
  addStoreOrderNotificationLog,
  getCurrentStoreUser,
  getStoreOrder,
  listStoreOrders,
  logoutStore,
  searchStoreCustomers,
  searchStoreOrderProducts,
  sendStoreOrderPickupNotification,
  StoreApiError,
  StoreCustomerSummary,
  StorePaginationMeta,
  StoreProductOption,
  StoreSalesOrder,
  StoreSalesOrderPayment,
  StoreSalesOrderPayload,
  StoreUser,
  updateStoreOrder,
  voidStoreOrder,
} from "./api";

const defaultPagination: StorePaginationMeta = { Page: 1, PerPage: 20, Total: 0, TotalPages: 1 };
const fallbackStatuses = [
  { Key: "PendingHeadquarters", Label: "待總部確認" },
  { Key: "PendingEngineering", Label: "待工程部備貨" },
  { Key: "WaitingPurchase", Label: "待廠商採購" },
  { Key: "PreparingOutbound", Label: "待出庫" },
  { Key: "ShippedToStore", Label: "已出庫到店" },
  { Key: "ReadyForPickup", Label: "可通知取件" },
  { Key: "Completed", Label: "已交付" },
  { Key: "Cancelled", Label: "已取消" },
  { Key: "Voided", Label: "已作廢" },
];
const fallbackPayments = [
  { Key: "Unpaid", Label: "未收款" },
  { Key: "DepositPaid", Label: "已收訂金" },
  { Key: "Paid", Label: "已全額" },
  { Key: "BalanceDue", Label: "尾款待收" },
];
const fallbackFulfillments = [
  { Key: "Headquarters", Label: "總部出貨" },
  { Key: "StoreStock", Label: "門市現貨交付" },
];
const paymentMethods = [
  { Key: "Cash", Label: "現金" },
  { Key: "CreditCard", Label: "信用卡" },
  { Key: "BankTransfer", Label: "匯款" },
  { Key: "LinePay", Label: "LINE Pay" },
  { Key: "Other", Label: "其他" },
];
const invoiceTypes = [
  { Key: "Pending", Label: "待補發票資料" },
  { Key: "Personal", Label: "二聯式" },
  { Key: "Company", Label: "三聯式 / 統編" },
  { Key: "Carrier", Label: "載具" },
  { Key: "Donation", Label: "捐贈" },
];
const deliveryChecklistKeys = ["ProductConfirmed", "SerialConfirmed", "AccessoriesConfirmed", "WarrantyExplained", "PaymentConfirmed", "CustomerAccepted"];
const fallbackDeliveryChecklistLabels: Record<string, string> = {
  ProductConfirmed: "商品規格確認",
  SerialConfirmed: "機身序號 / 耳側確認",
  AccessoriesConfirmed: "配件與包裝確認",
  WarrantyExplained: "保固與後續服務說明",
  PaymentConfirmed: "付款狀態確認",
  CustomerAccepted: "客戶簽收確認",
};
const notificationMethods = [
  { Key: "Phone", Label: "電話" },
  { Key: "Line", Label: "LINE" },
  { Key: "Sms", Label: "簡訊" },
  { Key: "Email", Label: "Email" },
  { Key: "Other", Label: "其他" },
];
const notificationStatuses = [
  { Key: "Sent", Label: "已通知" },
  { Key: "NoAnswer", Label: "未接通" },
  { Key: "Agreed", Label: "已約定" },
  { Key: "Rescheduled", Label: "改約" },
  { Key: "Failed", Label: "失敗" },
];
const orderPurposeOptions = [
  { Key: "Sale", Label: "一般銷售" },
  { Key: "Trial", Label: "試戴/借出" },
  { Key: "WarrantyReplacement", Label: "保固更換" },
  { Key: "Gift", Label: "贈品/活動" },
  { Key: "ServicePart", Label: "補件/服務" },
  { Key: "Test", Label: "測試單" },
  { Key: "Other", Label: "其他" },
];
const zeroPriceReasonOptions = [
  { Key: "TrialProduct", Label: "試戴/借出商品" },
  { Key: "WarrantyReplacement", Label: "保固更換" },
  { Key: "Gift", Label: "活動贈品" },
  { Key: "ServicePart", Label: "客訴/服務補件" },
  { Key: "ApprovedDiscount", Label: "主管核准折讓" },
  { Key: "TestData", Label: "測試資料" },
  { Key: "Other", Label: "其他" },
];

function purposeFromZeroPriceReason(reason: string) {
  const map: Record<string, string> = {
    TrialProduct: "Trial",
    WarrantyReplacement: "WarrantyReplacement",
    Gift: "Gift",
    ServicePart: "ServicePart",
    ApprovedDiscount: "Other",
    TestData: "Test",
    Other: "Other",
  };
  return map[reason] || "";
}

type DraftItem = {
  ProductId: string;
  VariantId: string;
  DisplayName: string;
  Sku: string;
  Stock?: number | null;
  UnitPrice: number;
  DealPrice: number;
  Quantity: number;
  ProductUsageType?: string;
  ProductUsageTypeLabel?: string;
  AllowZeroPriceOrder?: boolean;
  SerialNumber: string;
  EarSide: string;
  WarrantyStartDate: string;
};

type OrderFilterOverrides = Partial<{
  Keyword: string;
  Status: string;
  PaymentStatus: string;
  DateFrom: string;
  DateTo: string;
  PerPage: number;
}>;

function emptyForm(locationId = ""): StoreSalesOrderPayload {
  return {
    LocationId: locationId,
    CustomerName: "",
    CustomerPhone: "",
    CustomerEmail: "",
    PaymentStatus: "DepositPaid",
    FulfillmentType: "Headquarters",
    OrderPurpose: "Sale",
    ZeroPriceReason: "",
    ZeroPriceNote: "",
    DeliveryCompleted: false,
    DepositAmount: 0,
    InitialPaymentAmount: 0,
    InitialPaymentMethod: "Cash",
    InitialPaymentNote: "",
    InvoiceType: "Pending",
    InvoiceTaxId: "",
    InvoiceTitle: "",
    InvoiceCarrier: "",
    InvoiceDonationCode: "",
    InvoiceNote: "",
    ExpectedPickupDate: "",
    DeliveryNote: "",
    CustomerNote: "",
    InternalNote: "",
    Items: [],
  };
}

function today() {
  return new Date().toISOString().slice(0, 10);
}

function currentYearStart() {
  return `${new Date().getFullYear()}-01-01`;
}

function currentYearEnd() {
  return `${new Date().getFullYear()}-12-31`;
}

function money(value?: number | null) {
  return `NT$ ${Number(value || 0).toLocaleString()}`;
}

function earSideLabel(value?: string | null) {
  const labels: Record<string, string> = {
    Left: "左耳",
    Right: "右耳",
    Both: "雙耳",
    Accessory: "配件",
  };
  return value ? labels[value] || value : "-";
}

function text(value?: string | number | null) {
  return value === undefined || value === null || value === "" ? "-" : String(value);
}

function escapeHtml(value?: string | number | null) {
  return text(value)
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function nl2br(value?: string | null) {
  return escapeHtml(value).replaceAll("\n", "<br />");
}

function printDocument(title: string, body: string) {
  const frame = document.createElement("iframe");
  frame.setAttribute("title", title);
  frame.style.position = "fixed";
  frame.style.right = "0";
  frame.style.bottom = "0";
  frame.style.width = "0";
  frame.style.height = "0";
  frame.style.border = "0";
  frame.style.opacity = "0";
  document.body.appendChild(frame);
  const frameDocument = frame.contentWindow?.document;
  if (!frameDocument) {
    document.body.removeChild(frame);
    window.print();
    return;
  }
  frameDocument.open();
  frameDocument.write(`<!doctype html>
<html lang="zh-Hant">
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(title)}</title>
  <style>
    * { box-sizing: border-box; }
    body { margin: 0; background: #fff; color: #1f352c; font-family: "Noto Sans TC", "Microsoft JhengHei", Arial, sans-serif; }
    .sheet { width: 210mm; min-height: 297mm; margin: 0 auto; background: #fff; padding: 14mm 15mm; }
    .topline { height: 5px; background: #315746; margin: -14mm -15mm 12mm; }
    .header { display: grid; grid-template-columns: 1fr auto; gap: 24px; align-items: start; border-bottom: 1.5px solid #315746; padding-bottom: 10px; }
    .brand { font-size: 23px; font-weight: 900; letter-spacing: .08em; }
    .sub { margin-top: 4px; color: #6f7e76; font-size: 11px; font-weight: 700; letter-spacing: .16em; text-transform: uppercase; }
    .doc-title { text-align: right; font-size: 25px; font-weight: 900; letter-spacing: .08em; }
    .doc-no { margin-top: 5px; color: #59665f; font-size: 12px; font-weight: 800; }
    .meta { display: grid; grid-template-columns: repeat(4, 1fr); border: 1px solid #9aaba2; border-bottom: 0; margin-top: 12px; }
    .meta .cell { min-height: 42px; border-right: 1px solid #9aaba2; border-bottom: 1px solid #9aaba2; padding: 6px 8px; }
    .meta .cell:nth-child(4n) { border-right: 0; }
    .meta .wide { grid-column: span 2; }
    .label { color: #66756d; font-size: 10px; font-weight: 900; letter-spacing: .04em; }
    .value { margin-top: 4px; font-size: 13px; font-weight: 800; line-height: 1.45; word-break: break-word; }
    .section { margin-top: 14px; page-break-inside: avoid; }
    .section-title { display: flex; align-items: center; gap: 7px; margin: 0 0 7px; font-size: 14px; font-weight: 900; color: #315746; letter-spacing: .04em; }
    .section-title::before { content: ""; display: block; width: 4px; height: 16px; background: #315746; border-radius: 2px; }
    .amounts { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; }
    .amount { border: 1.5px solid #315746; padding: 9px 10px; min-height: 56px; }
    .amount .value { font-size: 18px; color: #17382b; }
    table { width: 100%; border-collapse: collapse; font-size: 11px; table-layout: fixed; }
    th { background: #edf3ef; color: #384c43; text-align: left; font-weight: 900; }
    th, td { border: 1px solid #9aaba2; padding: 7px; vertical-align: top; word-break: break-word; }
    .right { text-align: right; }
    .note { border: 1px solid #9aaba2; padding: 9px; color: #43554c; font-size: 12px; line-height: 1.65; min-height: 64px; }
    .check-mark { display: inline-block; width: 11px; height: 11px; margin-right: 6px; border: 1.5px solid #203d32; vertical-align: -1px; }
    .check-mark.on {
      background: #203d32 !important;
      box-shadow: inset 0 0 0 12px #203d32;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    .notice { margin-top: 12px; color: #66756d; font-size: 10px; line-height: 1.7; }
    .signatures { display: grid; grid-template-columns: repeat(3, 1fr); gap: 18px; margin-top: 34px; }
    .sign { border-top: 1px solid #5d6c64; padding-top: 8px; color: #43554c; font-size: 12px; font-weight: 900; text-align: center; }
    @page { size: A4; margin: 0; }
    @media print { body { background: #fff; } .sheet { width: auto; min-height: auto; margin: 0; } }
  </style>
</head>
<body><main class="sheet"><div class="topline"></div>${body}</main></body>
</html>`);
  frameDocument.close();
  const cleanup = () => window.setTimeout(() => frame.remove(), 800);
  frame.onload = () => {
    frame.contentWindow?.focus();
    frame.contentWindow?.print();
    frame.contentWindow?.addEventListener("afterprint", cleanup, { once: true });
    window.setTimeout(cleanup, 3000);
  };
}

function orderItemsRows(order: StoreSalesOrder) {
  return (order.Items || []).map((item) => `
    <tr>
      <td>${escapeHtml(item.ProductName)}${item.VariantLabel ? ` / ${escapeHtml(item.VariantLabel)}` : ""}</td>
      <td>${escapeHtml(item.Sku)}</td>
      <td>${escapeHtml(item.SerialNumber)}</td>
      <td>${escapeHtml(earSideLabel(item.EarSide))}</td>
      <td>${escapeHtml(item.WarrantyStartDate)}</td>
      <td class="right">${escapeHtml(money(item.DealPrice))}</td>
      <td class="right">${escapeHtml(item.Quantity)}</td>
      <td class="right">${escapeHtml(money(item.LineTotal))}</td>
    </tr>`).join("");
}

function deliveryChecklistEntries(order: StoreSalesOrder) {
  return deliveryChecklistKeys.map((key) => ({
    Key: key,
    Label: order.DeliveryChecklist?.[key]?.Label || fallbackDeliveryChecklistLabels[key],
    Checked: Boolean(order.DeliveryChecklist?.[key]?.Checked),
  }));
}

function zeroPriceAuditText(order: StoreSalesOrder) {
  return [order.ZeroPriceReasonLabel || order.ZeroPriceReason, order.ZeroPriceNote].filter(Boolean).join(" / ") || "-";
}

function orderAuditMetaCells(order: StoreSalesOrder) {
  return `
      <div class="cell"><div class="label">訂單性質</div><div class="value">${escapeHtml(order.OrderPurposeLabel || order.OrderPurpose || "一般銷售")}</div></div>
      <div class="cell wide"><div class="label">0 元原因 / 備註</div><div class="value">${escapeHtml(zeroPriceAuditText(order))}</div></div>`;
}

function orderPrintHtml(order: StoreSalesOrder) {
  const rows = (order.Items || []).map((item) => `
    <tr>
      <td>${escapeHtml(item.ProductName)}${item.VariantLabel ? ` / ${escapeHtml(item.VariantLabel)}` : ""}</td>
      <td>${escapeHtml(item.Sku)}</td>
      <td>${escapeHtml(item.SerialNumber)}</td>
      <td>${escapeHtml(earSideLabel(item.EarSide))}</td>
      <td>${escapeHtml(item.WarrantyStartDate)}</td>
      <td class="right">${escapeHtml(money(item.DealPrice))}</td>
      <td class="right">${escapeHtml(item.Quantity)}</td>
      <td class="right">${escapeHtml(money(item.LineTotal))}</td>
    </tr>`).join("");
  const payments = (order.Payments || []).map((item) => `
    <tr>
      <td>${escapeHtml(item.PaymentTypeLabel || item.PaymentType)}</td>
      <td>${escapeHtml(item.PaymentMethodLabel || item.PaymentMethod)}</td>
      <td>${escapeHtml(item.StoreUserName)}</td>
      <td>${escapeHtml(item.ReceivedAt)}</td>
      <td>${escapeHtml(item.Note)}</td>
      <td class="right">${escapeHtml(money(item.Amount))}</td>
    </tr>`).join("");
  return `
    <div class="header">
      <div><div class="brand">富聆聽健康</div><div class="sub">Store Portal</div></div>
      <div><div class="doc-title">訂購確認單</div><div class="doc-no">訂單編號：${escapeHtml(order.OrderNumber)}</div><div class="doc-no">列印日：${escapeHtml(new Date().toLocaleDateString("zh-TW"))}</div></div>
    </div>
    <section class="meta">
      <div class="cell"><div class="label">客戶姓名</div><div class="value">${escapeHtml(order.CustomerName)}</div></div>
      <div class="cell"><div class="label">客戶電話</div><div class="value">${escapeHtml(order.CustomerPhone)}</div></div>
      <div class="cell wide"><div class="label">Email</div><div class="value">${escapeHtml(order.CustomerEmail)}</div></div>
      <div class="cell"><div class="label">成交門市</div><div class="value">${escapeHtml(order.LocationName)}</div></div>
      <div class="cell"><div class="label">建立同仁</div><div class="value">${escapeHtml(order.StoreUserName)}</div></div>
      <div class="cell"><div class="label">流程狀態</div><div class="value">${escapeHtml(order.StatusLabel || order.Status)}</div></div>
      <div class="cell"><div class="label">付款狀態</div><div class="value">${escapeHtml(order.PaymentStatusLabel || order.PaymentStatus)}</div></div>
      <div class="cell"><div class="label">交付方式</div><div class="value">${escapeHtml(order.FulfillmentTypeLabel)}</div></div>
      <div class="cell"><div class="label">預計取件日</div><div class="value">${escapeHtml(order.ExpectedPickupDate)}</div></div>
      <div class="cell"><div class="label">交付時間</div><div class="value">${escapeHtml(order.DeliveredAt)}</div></div>
      <div class="cell"><div class="label">建立時間</div><div class="value">${escapeHtml(order.CreatedAt)}</div></div>
      ${orderAuditMetaCells(order)}
    </section>
    <section class="section amounts">
      <div class="amount"><div class="label">訂單總額</div><div class="value">${escapeHtml(money(order.TotalAmount))}</div></div>
      <div class="amount"><div class="label">已收金額</div><div class="value">${escapeHtml(money(order.DepositAmount))}</div></div>
      <div class="amount"><div class="label">剩餘待付金額</div><div class="value">${escapeHtml(money(order.BalanceAmount))}</div></div>
    </section>
    <section class="section"><h2 class="section-title">商品明細</h2><table><thead><tr><th style="width:26%">商品</th><th style="width:12%">SKU</th><th style="width:15%">序號</th><th style="width:8%">耳側</th><th style="width:12%">保固起算</th><th class="right" style="width:10%">成交價</th><th class="right" style="width:7%">數量</th><th class="right" style="width:10%">小計</th></tr></thead><tbody>${rows || '<tr><td colspan="8">尚無商品明細</td></tr>'}</tbody></table></section>
    <section class="section"><h2 class="section-title">收款紀錄</h2><table><thead><tr><th style="width:12%">類型</th><th style="width:12%">方式</th><th style="width:14%">收款人</th><th style="width:18%">時間</th><th>備註</th><th class="right" style="width:12%">金額</th></tr></thead><tbody>${payments || '<tr><td colspan="6">尚無收款紀錄</td></tr>'}</tbody></table></section>
    <section class="section"><h2 class="section-title">客戶備註</h2><div class="note">${nl2br(order.CustomerNote)}</div></section>
    <p class="notice">本確認單用於確認訂購商品、成交金額與目前收款狀態；實際出貨、庫存扣帳與採購入庫流程依總部/工程部後續作業紀錄為準。</p>
    <div class="signatures"><div class="sign">門市經手人</div><div class="sign">客戶確認</div><div class="sign">主管覆核</div></div>
  `;
}

function receiptPrintHtml(order: StoreSalesOrder, payment: StoreSalesOrderPayment) {
  return `
    <div class="header">
      <div><div class="brand">富聆聽健康</div><div class="sub">Store Portal</div></div>
      <div><div class="doc-title">收款收據</div><div class="doc-no">收據編號：${escapeHtml(payment.StoreSalesOrderPaymentId)}</div><div class="doc-no">訂單編號：${escapeHtml(order.OrderNumber)}</div></div>
    </div>
    <section class="meta">
      <div class="cell"><div class="label">客戶姓名</div><div class="value">${escapeHtml(order.CustomerName)}</div></div>
      <div class="cell"><div class="label">客戶電話</div><div class="value">${escapeHtml(order.CustomerPhone)}</div></div>
      <div class="cell"><div class="label">成交門市</div><div class="value">${escapeHtml(order.LocationName)}</div></div>
      <div class="cell"><div class="label">收款人員</div><div class="value">${escapeHtml(payment.StoreUserName)}</div></div>
      <div class="cell"><div class="label">收款項目</div><div class="value">${escapeHtml(payment.PaymentTypeLabel || payment.PaymentType)}</div></div>
      <div class="cell"><div class="label">收款方式</div><div class="value">${escapeHtml(payment.PaymentMethodLabel || payment.PaymentMethod)}</div></div>
      <div class="cell wide"><div class="label">收款時間</div><div class="value">${escapeHtml(payment.ReceivedAt || payment.CreatedAt)}</div></div>
      ${orderAuditMetaCells(order)}
    </section>
    <section class="section amounts">
      <div class="amount"><div class="label">本次實收金額</div><div class="value">${escapeHtml(money(payment.Amount))}</div></div>
      <div class="amount"><div class="label">訂單總額</div><div class="value">${escapeHtml(money(order.TotalAmount))}</div></div>
      <div class="amount"><div class="label">剩餘待付金額</div><div class="value">${escapeHtml(money(order.BalanceAmount))}</div></div>
    </section>
    <section class="section"><h2 class="section-title">商品摘要</h2><table><thead><tr><th style="width:42%">商品</th><th style="width:18%">SKU</th><th style="width:12%">數量</th><th class="right">小計</th></tr></thead><tbody>${(order.Items || []).map((item) => `<tr><td>${escapeHtml(item.ProductName)}${item.VariantLabel ? ` / ${escapeHtml(item.VariantLabel)}` : ""}</td><td>${escapeHtml(item.Sku)}</td><td>${escapeHtml(item.Quantity)}</td><td class="right">${escapeHtml(money(item.LineTotal))}</td></tr>`).join("") || '<tr><td colspan="4">尚無商品明細</td></tr>'}</tbody></table></section>
    <section class="section"><h2 class="section-title">收款備註</h2><div class="note">${nl2br(payment.Note)}</div></section>
    <p class="notice">本收據依系統收款流水產生，作為本次收款留存；若需正式發票，請依發票資料與公司開立流程辦理。</p>
    <div class="signatures"><div class="sign">收款人</div><div class="sign">客戶簽收</div><div class="sign">主管覆核</div></div>
  `;
}

function deliveryPrintHtml(order: StoreSalesOrder) {
  const rows = orderItemsRows(order);
  const checklistRows = deliveryChecklistEntries(order).map((item) => `
    <tr>
      <td>${escapeHtml(item.Label)}</td>
      <td style="width:18%; font-weight:900;"><span class="check-mark ${item.Checked ? "on" : ""}"></span>${item.Checked ? "已確認" : "未確認"}</td>
    </tr>
  `).join("");
  return `
    <div class="header">
      <div><div class="brand">富聆聽健康</div><div class="sub">Store Portal</div></div>
      <div><div class="doc-title">交付確認單</div><div class="doc-no">訂單編號：${escapeHtml(order.OrderNumber)}</div><div class="doc-no">列印日：${escapeHtml(new Date().toLocaleDateString("zh-TW"))}</div></div>
    </div>
    <section class="meta">
      <div class="cell"><div class="label">客戶姓名</div><div class="value">${escapeHtml(order.CustomerName)}</div></div>
      <div class="cell"><div class="label">客戶電話</div><div class="value">${escapeHtml(order.CustomerPhone)}</div></div>
      <div class="cell"><div class="label">交付門市</div><div class="value">${escapeHtml(order.LocationName)}</div></div>
      <div class="cell"><div class="label">交付方式</div><div class="value">${escapeHtml(order.FulfillmentTypeLabel)}</div></div>
      <div class="cell"><div class="label">交付時間</div><div class="value">${escapeHtml(order.DeliveredAt)}</div></div>
      <div class="cell"><div class="label">付款狀態</div><div class="value">${escapeHtml(order.PaymentStatusLabel || order.PaymentStatus)}</div></div>
      <div class="cell"><div class="label">剩餘待付金額</div><div class="value">${escapeHtml(money(order.BalanceAmount))}</div></div>
      <div class="cell"><div class="label">建立同仁</div><div class="value">${escapeHtml(order.StoreUserName)}</div></div>
      ${orderAuditMetaCells(order)}
    </section>
    <section class="section"><h2 class="section-title">交付商品明細</h2><table><thead><tr><th style="width:26%">商品</th><th style="width:12%">SKU</th><th style="width:15%">序號</th><th style="width:8%">耳側</th><th style="width:12%">保固起算</th><th class="right" style="width:10%">成交價</th><th class="right" style="width:7%">數量</th><th class="right" style="width:10%">小計</th></tr></thead><tbody>${rows || '<tr><td colspan="8">尚無商品明細</td></tr>'}</tbody></table></section>
    <section class="section"><h2 class="section-title">交付確認項目</h2><table><tbody>${checklistRows}</tbody></table></section>
    <section class="section"><h2 class="section-title">客戶備註</h2><div class="note">${nl2br(order.CustomerNote)}</div></section>
    <p class="notice">本確認單作為商品交付、序號、保固起算與客戶簽收留存；若商品需後續調整，請另建服務紀錄。</p>
    <div class="signatures"><div class="sign">交付人員</div><div class="sign">客戶簽收</div><div class="sign">主管覆核</div></div>
  `;
}

function orderTitle(order: StoreSalesOrder) {
  return order.FirstProductName ? `${order.FirstProductName}${order.FirstVariantLabel ? ` / ${order.FirstVariantLabel}` : ""}` : "門市訂單";
}

function InfoTile({ label, tone = "neutral", value }: { label: string; tone?: "neutral" | "strong"; value?: string | number | null }) {
  return (
    <div className={`min-w-0 rounded-md border px-3 py-2 ${tone === "strong" ? "border-[#c7dccd] bg-[#f0f7f2]" : "border-[#edf0ee] bg-[#fbfcfb]"}`}>
      <p className="text-xs font-bold text-[#7b877f]">{label}</p>
      <p className="mt-1 min-w-0 break-all text-sm font-extrabold leading-6 text-[#203d32]">{value || "-"}</p>
    </div>
  );
}

function StatusFilterCard({ active, label, onClick, value }: { active: boolean; label: string; onClick: () => void; value: string }) {
  return (
    <button
      className={`min-w-0 rounded-md border px-4 py-3 text-left transition hover:border-[#9ac8aa] hover:bg-[#f1f8f3] ${
        active ? "border-[#9ac8aa] bg-[#f1f8f3] shadow-[inset_0_0_0_1px_rgba(49,87,70,.08)]" : "border-[#edf0ee] bg-white"
      }`}
      onClick={onClick}
      type="button"
    >
      <span className="block text-sm font-bold text-[#7b877f]">{label}</span>
      <span className="mt-2 block text-lg font-extrabold text-[#203d32]">{value}</span>
    </button>
  );
}

function DeliveryChecklistSummary({ order }: { order: StoreSalesOrder }) {
  const entries = deliveryChecklistEntries(order);
  const checkedCount = entries.filter((item) => item.Checked).length;
  const hasRecord = Boolean(order.DeliveryCheckedAt || checkedCount > 0);

  if (!hasRecord) return null;

  return (
    <div className="rounded-md border border-[#dfe6e1] bg-white p-4">
      <div className="mb-3 flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h3 className="flex items-center gap-2 font-extrabold text-[#203d32]">
            <ListChecks size={18} />
            交付確認紀錄
          </h3>
          <p className="mt-1 text-sm font-medium text-[#7b877f]">
            {order.DeliveryCheckedAt ? `確認時間：${order.DeliveryCheckedAt}` : "尚未記錄確認時間"}
          </p>
        </div>
        <span className="text-sm font-bold text-[#59665f]">{checkedCount} / {entries.length} 項完成</span>
      </div>
      <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
        {entries.map((item) => (
          <div className={`flex min-h-11 items-center gap-2 rounded-md border px-3 py-2 text-sm font-bold ${item.Checked ? "border-[#cfe3d5] bg-[#f2f9f4] text-[#245438]" : "border-[#edf0ee] bg-[#fbfcfb] text-[#7b877f]"}`} key={item.Key}>
            <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded border border-current text-xs">{item.Checked ? "✓" : ""}</span>
            <span className="min-w-0 break-words">{item.Label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function OrderCard({ item, onSelect, selected }: { item: StoreSalesOrder; onSelect: () => void; selected: boolean }) {
  const isVoided = item.Status === "Voided";
  return (
    <button
      className={`w-full rounded-md border p-4 text-left transition hover:border-[#b8c8bf] hover:shadow-[0_8px_22px_rgba(32,61,50,.08)] ${
        selected ? "border-[#9ac8aa] ring-2 ring-[#9ac8aa]/20" : "border-[#dfe6e1]"
      } ${isVoided ? "bg-[#fff8f7] opacity-90" : "bg-white"}`}
      onClick={onSelect}
      type="button"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-sm font-bold text-[#7b877f]">{item.OrderNumber}</p>
          <p className="mt-1 truncate text-base font-extrabold text-[#203d32]">{item.CustomerName}</p>
          <p className="mt-1 text-sm font-bold text-[#59665f]">{item.CustomerPhone}</p>
        </div>
        <span className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-bold ${isVoided ? "bg-[#fdebea] text-[#9a2d2d]" : "bg-[#e9f4ec] text-[#245438]"}`}>{item.StatusLabel || item.Status}</span>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2 text-xs font-bold text-[#59665f]">
        <span className="truncate rounded bg-[#f4f6f5] px-2 py-1">{orderTitle(item)}</span>
        <span className="rounded bg-[#f4f6f5] px-2 py-1">{money(item.TotalAmount)}</span>
      </div>
    </button>
  );
}

export default function StoreOrders() {
  const navigate = useNavigate();
  const { storeSalesOrderId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [user, setUser] = useState<StoreUser | null>(null);
  const [currentLocationId, setCurrentLocationId] = useState<string | null>(() => rememberedStoreLocationId());
  const [orders, setOrders] = useState<StoreSalesOrder[]>([]);
  const [order, setOrder] = useState<StoreSalesOrder | null>(null);
  const [pagination, setPagination] = useState<StorePaginationMeta>(defaultPagination);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [statusOptions, setStatusOptions] = useState(fallbackStatuses);
  const [paymentOptions, setPaymentOptions] = useState(fallbackPayments);
  const [fulfillmentOptions, setFulfillmentOptions] = useState(fallbackFulfillments);
  const [keyword, setKeyword] = useState("");
  const [status, setStatus] = useState(searchParams.get("Status") || searchParams.get("status") || "");
  const [paymentStatus, setPaymentStatus] = useState(searchParams.get("PaymentStatus") || searchParams.get("paymentStatus") || "");
  const [dateFrom, setDateFrom] = useState(searchParams.get("DateFrom") || currentYearStart());
  const [dateTo, setDateTo] = useState(searchParams.get("DateTo") || currentYearEnd());
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [messageTone, setMessageTone] = useState<"error" | "success">("error");
  const [form, setForm] = useState<StoreSalesOrderPayload>(emptyForm());
  const [draftItems, setDraftItems] = useState<DraftItem[]>([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editingOrderId, setEditingOrderId] = useState<string | null>(null);
  const [voidPanelOpen, setVoidPanelOpen] = useState(false);
  const [voidReason, setVoidReason] = useState("");
  const [paymentPanelOpen, setPaymentPanelOpen] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState("Cash");
  const [paymentNote, setPaymentNote] = useState("");
  const [paymentMessage, setPaymentMessage] = useState("");
  const [paymentCompleteOrder, setPaymentCompleteOrder] = useState(true);
  const [deliveryChecklist, setDeliveryChecklist] = useState<Record<string, boolean>>({});
  const [sendingNotification, setSendingNotification] = useState(false);
  const [receiptNote, setReceiptNote] = useState("");
  const [notificationLogOpen, setNotificationLogOpen] = useState(false);
  const [notificationMethod, setNotificationMethod] = useState("Phone");
  const [notificationStatus, setNotificationStatus] = useState("Sent");
  const [notificationBody, setNotificationBody] = useState("");
  const [customerOptions, setCustomerOptions] = useState<StoreCustomerSummary[]>([]);
  const [selectedCustomerKey, setSelectedCustomerKey] = useState("");
  const [selectedCustomerSnapshot, setSelectedCustomerSnapshot] = useState<{ Name: string; Phone: string } | null>(null);
  const [productOptions, setProductOptions] = useState<StoreProductOption[]>([]);
  const [customerSearching, setCustomerSearching] = useState(false);
  const [productSearching, setProductSearching] = useState(false);
  const [productKeyword, setProductKeyword] = useState("");
  const paymentPanelRef = useRef<HTMLDivElement | null>(null);

  const effectiveLocationId = useMemo(
    () => preferredStoreLocationId(user, currentLocationId),
    [currentLocationId, user],
  );
  const totalAmount = useMemo(() => draftItems.reduce((sum, item) => sum + item.DealPrice * item.Quantity, 0), [draftItems]);
  const initialPaymentAmount = Number(form.InitialPaymentAmount ?? form.DepositAmount ?? 0);
  const balanceAmount = Math.max(0, totalAmount - initialPaymentAmount);
  const draftPaymentStatus = totalAmount === 0 ? "Paid" : initialPaymentAmount <= 0 ? "Unpaid" : balanceAmount === 0 ? "Paid" : "DepositPaid";
  const draftPaymentStatusLabel = paymentOptions.find((item) => item.Key === draftPaymentStatus)?.Label || draftPaymentStatus;
  const hasZeroPriceItem = draftItems.some((item) => item.DealPrice === 0);
  const requiresZeroPriceAudit = draftItems.length > 0 && (hasZeroPriceItem || totalAmount === 0);
  const inferredOrderPurpose = form.OrderPurpose === "Sale" ? purposeFromZeroPriceReason(form.ZeroPriceReason || "") || form.OrderPurpose : form.OrderPurpose || "Sale";
  const zeroPriceBlockedItems = draftItems.filter((item) => item.DealPrice === 0 && !item.AllowZeroPriceOrder && inferredOrderPurpose === "Sale");
  const zeroPriceAuditReady = !requiresZeroPriceAudit || (inferredOrderPurpose !== "Sale" && Boolean(form.ZeroPriceReason));
  const zeroPriceAuditMissingText = requiresZeroPriceAudit
    ? inferredOrderPurpose === "Sale"
      ? "請先將訂單性質改為試戴、保固更換、贈品、補件、測試或其他。"
      : !form.ZeroPriceReason
        ? "請選擇 0 元原因。"
        : ""
    : "";
  const canWriteOrders = hasStorePermission(user, "store-orders-write");
  const canVoidOrders = hasStorePermission(user, "store-orders-void");
  const orderCanCompleteDelivery = Boolean(order && (order.Status === "ShippedToStore" || order.Status === "ReadyForPickup" || order.FulfillmentType === "StoreStock"));
  const orderDeliveryPrintable = Boolean(order && (order.DeliveredAt || order.Status === "Completed"));
  const paymentIsZeroBalance = Boolean(order && (order.BalanceAmount || 0) === 0);
  const paymentWillSettleBalance = Boolean(order && Math.max(0, (order.BalanceAmount || 0) - Math.max(0, paymentAmount || 0)) === 0);
  const paymentCanCompleteOrder = orderCanCompleteDelivery && paymentWillSettleBalance;
  const notificationHistory = useMemo(() => order?.Notifications || [], [order]);
  const pickupNotification = useMemo(
    () => (order?.Notifications || []).find((item) => item.NotificationType === "PickupReady" && item.Method === "Email" && item.Status === "Sent") || null,
    [order],
  );
  const pickupNotificationCount = useMemo(
    () => (order?.Notifications || []).filter((item) => item.NotificationType === "PickupReady" && item.Method === "Email" && item.Status === "Sent").length,
    [order],
  );
  const orderCanSendPickupNotification = Boolean(order && (order.Status === "ShippedToStore" || order.Status === "ReadyForPickup"));

  function deliveryChecklistFromOrder(nextOrder: StoreSalesOrder | null) {
    return deliveryChecklistKeys.reduce<Record<string, boolean>>((result, key) => {
      result[key] = Boolean(nextOrder?.DeliveryChecklist?.[key]?.Checked);
      return result;
    }, {});
  }

  function notificationMethodLabel(value: string) {
    return notificationMethods.find((item) => item.Key === value)?.Label || value;
  }

  function notificationStatusLabel(value: string) {
    return notificationStatuses.find((item) => item.Key === value)?.Label || value;
  }

  async function loadList(locationId?: string | null, page = pagination.Page, filters: OrderFilterOverrides = {}) {
    setLoading(true);
    setMessage("");
    setMessageTone("error");
    try {
      const me = await getCurrentStoreUser();
      setUser(me.User);
      const nextLocationId = preferredStoreLocationId(me.User, locationId || currentLocationId);
      setCurrentLocationId(nextLocationId);
      setForm((current) => ({ ...current, LocationId: current.LocationId || nextLocationId || "" }));
      const data = await listStoreOrders({
        LocationId: nextLocationId,
        Keyword: (filters.Keyword ?? keyword).trim(),
        Status: filters.Status ?? status,
        PaymentStatus: filters.PaymentStatus ?? paymentStatus,
        DateFrom: filters.DateFrom ?? dateFrom,
        DateTo: filters.DateTo ?? dateTo,
        Page: page,
        PerPage: filters.PerPage ?? pagination.PerPage,
      });
      setOrders(data.Orders || []);
      setCounts(data.Counts || {});
      setPagination(data.Pagination || defaultPagination);
      if (data.StatusOptions?.length) setStatusOptions(data.StatusOptions);
      if (data.PaymentStatusOptions?.length) setPaymentOptions(data.PaymentStatusOptions);
      if (data.FulfillmentTypeOptions?.length) setFulfillmentOptions(data.FulfillmentTypeOptions);
    } catch (error) {
      setMessageTone("error");
      setMessage(error instanceof Error ? error.message : "訂單載入失敗。");
      if (error instanceof StoreApiError && error.status === 401) navigate("/store-login", { replace: true });
    } finally {
      setLoading(false);
    }
  }

  async function loadDetail(id?: string) {
    if (!id) {
      setOrder(null);
      return;
    }
    setDetailLoading(true);
    setMessage("");
    setMessageTone("error");
    try {
      const data = await getStoreOrder(id);
      if (effectiveLocationId && data.Order.LocationId !== effectiveLocationId) {
        setOrder(null);
        setMessage("此訂單不屬於目前選取門市，已回到訂單列表。");
        navigate("/store/orders", { replace: true });
        return;
      }
      setOrder(data.Order);
      if (!paymentPanelOpen) setDeliveryChecklist(deliveryChecklistFromOrder(data.Order));
    } catch (error) {
      setMessageTone("error");
      setMessage(error instanceof Error ? error.message : "訂單詳情載入失敗。");
      if (error instanceof StoreApiError && error.status === 401) navigate("/store-login", { replace: true });
    } finally {
      setDetailLoading(false);
    }
  }

  useEffect(() => {
    loadList(null, 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadDetail(storeSalesOrderId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeSalesOrderId, effectiveLocationId]);

  useEffect(() => {
    const next = new URLSearchParams();
    if (status) next.set("Status", status);
    if (keyword.trim()) next.set("Keyword", keyword.trim());
    if (paymentStatus) next.set("PaymentStatus", paymentStatus);
    if (dateFrom) next.set("DateFrom", dateFrom);
    if (dateTo) next.set("DateTo", dateTo);
    setSearchParams(next, { replace: true });
  }, [dateFrom, dateTo, keyword, paymentStatus, setSearchParams, status]);

  useEffect(() => {
    const query = form.CustomerPhone.trim() || form.CustomerName.trim();
    if (!effectiveLocationId || query.length < 2) {
      setCustomerOptions([]);
      return;
    }
    if (selectedCustomerKey && selectedCustomerSnapshot && selectedCustomerSnapshot.Name === form.CustomerName.trim() && selectedCustomerSnapshot.Phone === form.CustomerPhone.trim()) {
      setCustomerOptions([]);
      setCustomerSearching(false);
      return;
    }
    const timer = window.setTimeout(async () => {
      setCustomerSearching(true);
      try {
        const data = await searchStoreCustomers({ LocationId: effectiveLocationId, Keyword: query, Page: 1, PerPage: 6 });
        setCustomerOptions(data.Customers || []);
      } catch {
        setCustomerOptions([]);
      } finally {
        setCustomerSearching(false);
      }
    }, 250);
    return () => window.clearTimeout(timer);
  }, [effectiveLocationId, form.CustomerName, form.CustomerPhone, selectedCustomerKey, selectedCustomerSnapshot]);

  useEffect(() => {
    const query = productKeyword.trim();
    if (query.length < 1) {
      setProductOptions([]);
      return;
    }
    const timer = window.setTimeout(async () => {
      setProductSearching(true);
      try {
        const data = await searchStoreOrderProducts(query, 8);
        setProductOptions(data.Products || []);
      } catch {
        setProductOptions([]);
      } finally {
        setProductSearching(false);
      }
    }, 250);
    return () => window.clearTimeout(timer);
  }, [productKeyword]);

  async function handleLogout() {
    await logoutStore().catch(() => undefined);
    navigate("/store-login", { replace: true });
  }

  function resetLocationScopedState(locationId: string) {
    setOrder(null);
    setForm(emptyForm(locationId));
    setDraftItems([]);
    setFormOpen(false);
    setEditingOrderId(null);
    setVoidPanelOpen(false);
    setVoidReason("");
    setPaymentPanelOpen(false);
    setPaymentAmount(0);
    setPaymentNote("");
    setPaymentMessage("");
    setDeliveryChecklist({});
    setReceiptNote("");
    setNotificationLogOpen(false);
    setNotificationBody("");
    setCustomerOptions([]);
    setSelectedCustomerKey("");
    setSelectedCustomerSnapshot(null);
    setProductOptions([]);
    setProductKeyword("");
  }

  function handleLocationChange(locationId: string) {
    setCurrentLocationId(locationId);
    resetLocationScopedState(locationId);
    navigate("/store/orders");
    loadList(locationId, 1);
  }

  function handleSearch(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    loadList(effectiveLocationId, 1);
  }

  function resetFilters() {
    const nextDateFrom = currentYearStart();
    const nextDateTo = currentYearEnd();
    setKeyword("");
    setStatus("");
    setPaymentStatus("");
    setDateFrom(nextDateFrom);
    setDateTo(nextDateTo);
    loadList(effectiveLocationId, 1, { Keyword: "", Status: "", PaymentStatus: "", DateFrom: nextDateFrom, DateTo: nextDateTo });
  }

  function applyStatusFilter(nextStatus: string) {
    setStatus(nextStatus);
    setPaymentStatus("");
    loadList(effectiveLocationId, 1, { Status: nextStatus, PaymentStatus: "" });
  }

  function applyPaymentFilter(nextPaymentStatus: string) {
    setStatus("");
    setPaymentStatus(nextPaymentStatus);
    loadList(effectiveLocationId, 1, { Status: "", PaymentStatus: nextPaymentStatus });
  }

  function updateCustomerField(key: "CustomerName" | "CustomerPhone", value: string) {
    setSelectedCustomerKey(null);
    setSelectedCustomerSnapshot(null);
    setForm((current) => ({ ...current, [key]: value }));
  }

  function selectCustomer(item: StoreCustomerSummary) {
    setSelectedCustomerKey(item.CustomerKey);
    setSelectedCustomerSnapshot({ Name: item.Name, Phone: item.Phone });
    setForm((current) => ({ ...current, CustomerName: item.Name, CustomerPhone: item.Phone, CustomerEmail: item.Email || current.CustomerEmail || "" }));
    setCustomerOptions([]);
    setCustomerSearching(false);
  }

  function addProduct(item: StoreProductOption) {
    setDraftItems((current) => [
      ...current,
      {
        ProductId: item.ProductId,
        VariantId: item.VariantId || "",
        DisplayName: item.DisplayName,
        Sku: item.Sku,
        Stock: item.Stock,
        UnitPrice: Number(item.Price || 0),
        DealPrice: Number(item.Price || 0),
        Quantity: 1,
        ProductUsageType: item.ProductUsageType || "Sale",
        ProductUsageTypeLabel: item.ProductUsageTypeLabel || "一般銷售",
        AllowZeroPriceOrder: item.AllowZeroPriceOrder === true,
        SerialNumber: "",
        EarSide: "",
        WarrantyStartDate: "",
      },
    ]);
    setProductKeyword("");
    setProductOptions([]);
  }

  function updateDraftItem(index: number, key: "DealPrice" | "Quantity", value: number) {
    setDraftItems((current) => current.map((item, itemIndex) => (itemIndex === index ? { ...item, [key]: Math.max(key === "Quantity" ? 1 : 0, value || 0) } : item)));
  }

  function updateDraftItemText(index: number, key: "SerialNumber" | "EarSide" | "WarrantyStartDate", value: string) {
    setDraftItems((current) => current.map((item, itemIndex) => (itemIndex === index ? { ...item, [key]: value } : item)));
  }

  function removeDraftItem(index: number) {
    setDraftItems((current) => current.filter((_, itemIndex) => itemIndex !== index));
  }

  function startCreate() {
    if (!canWriteOrders) {
      setMessageTone("error");
      setMessage("此帳號沒有新增門市訂單權限。");
      return;
    }
    const currentQuery = searchParams.toString();
    setEditingOrderId(null);
    setOrder(null);
    setSelectedCustomerKey("");
    setSelectedCustomerSnapshot(null);
    setVoidPanelOpen(false);
    setPaymentPanelOpen(false);
    setForm(emptyForm(effectiveLocationId || ""));
    setDraftItems([]);
    setFormOpen(true);
    navigate(`/store/orders${currentQuery ? `?${currentQuery}` : ""}`);
  }

  function cancelEdit() {
    setEditingOrderId(null);
    setForm(emptyForm(effectiveLocationId || ""));
    setDraftItems([]);
    setFormOpen(false);
    if (editingOrderId === null) {
      const currentQuery = searchParams.toString();
      navigate(`/store/orders${currentQuery ? `?${currentQuery}` : ""}`);
    }
  }

  function startEdit(target: StoreSalesOrder) {
    if (!canWriteOrders) {
      setMessageTone("error");
      setMessage("此帳號沒有編輯門市訂單權限。");
      return;
    }
    setEditingOrderId(target.StoreSalesOrderId);
    setSelectedCustomerKey("");
    setSelectedCustomerSnapshot(null);
    setVoidPanelOpen(false);
    setPaymentPanelOpen(false);
    setFormOpen(true);
    setForm({
      LocationId: target.LocationId,
      CustomerName: target.CustomerName,
      CustomerPhone: target.CustomerPhone,
      CustomerEmail: target.CustomerEmail || "",
      PaymentStatus: target.PaymentStatus,
      FulfillmentType: target.FulfillmentType || "Headquarters",
      OrderPurpose: target.OrderPurpose || "Sale",
      ZeroPriceReason: target.ZeroPriceReason || "",
      ZeroPriceNote: target.ZeroPriceNote || "",
      DeliveryCompleted: target.FulfillmentType === "StoreStock",
      DepositAmount: target.DepositAmount,
      InitialPaymentAmount: target.DepositAmount,
      InitialPaymentMethod: target.Payments?.find((item) => item.PaymentType === "Deposit")?.PaymentMethod || "Cash",
      InitialPaymentNote: target.Payments?.find((item) => item.PaymentType === "Deposit")?.Note || "",
      InvoiceType: target.InvoiceType || "Pending",
      InvoiceTaxId: target.InvoiceTaxId || "",
      InvoiceTitle: target.InvoiceTitle || "",
      InvoiceCarrier: target.InvoiceCarrier || "",
      InvoiceDonationCode: target.InvoiceDonationCode || "",
      InvoiceNote: target.InvoiceNote || "",
      ExpectedPickupDate: target.ExpectedPickupDate || "",
      DeliveryNote: target.DeliveryNote || "",
      CustomerNote: target.CustomerNote || "",
      InternalNote: target.InternalNote || "",
      Items: [],
    });
    setDraftItems((target.Items || []).map((item) => ({
      ProductId: item.ProductId,
      VariantId: item.VariantId || "",
      DisplayName: `${item.ProductName}${item.VariantLabel ? ` / ${item.VariantLabel}` : ""}`,
      Sku: item.Sku || "",
      Stock: null,
      UnitPrice: item.UnitPrice,
      DealPrice: item.DealPrice,
      Quantity: item.Quantity,
      ProductUsageType: "Sale",
      ProductUsageTypeLabel: "一般銷售",
      AllowZeroPriceOrder: false,
      SerialNumber: item.SerialNumber || "",
      EarSide: item.EarSide || "",
      WarrantyStartDate: item.WarrantyStartDate || "",
    })));
  }

  async function handleSubmitOrder(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!canWriteOrders) {
      setMessageTone("error");
      setMessage("此帳號沒有新增或編輯門市訂單權限。");
      return;
    }
    if (requiresZeroPriceAudit && !zeroPriceAuditReady) {
      setMessageTone("error");
      setMessage(`0 元商品或 0 元訂單需要完成稽核資料。${zeroPriceAuditMissingText}`);
      return;
    }
    setSaving(true);
    setMessage("");
    setMessageTone("error");
    try {
      const payload: StoreSalesOrderPayload = {
        ...form,
        LocationId: form.LocationId || effectiveLocationId || "",
        PaymentStatus: draftPaymentStatus,
        DepositAmount: initialPaymentAmount,
        InitialPaymentAmount: initialPaymentAmount,
        InitialPaymentMethod: form.InitialPaymentMethod || "Cash",
        InitialPaymentNote: form.InitialPaymentNote || "",
        OrderPurpose: inferredOrderPurpose,
        ZeroPriceReason: requiresZeroPriceAudit ? form.ZeroPriceReason || "" : "",
        ZeroPriceNote: form.ZeroPriceNote || "",
        Items: draftItems.map((item) => ({ ProductId: item.ProductId, VariantId: item.VariantId, Quantity: item.Quantity, DealPrice: item.DealPrice, SerialNumber: item.SerialNumber, EarSide: item.EarSide, WarrantyStartDate: item.WarrantyStartDate })),
      };
      const data = editingOrderId ? await updateStoreOrder(editingOrderId, payload) : await createStoreOrder(payload);
      const nextDateFrom = currentYearStart();
      const nextDateTo = currentYearEnd();
      setForm(emptyForm(effectiveLocationId || ""));
      setDraftItems([]);
      setEditingOrderId(null);
      setFormOpen(false);
      setKeyword("");
      setStatus("");
      setPaymentStatus("");
      setDateFrom(nextDateFrom);
      setDateTo(nextDateTo);
      await loadList(effectiveLocationId, 1, { Keyword: "", Status: "", PaymentStatus: "", DateFrom: nextDateFrom, DateTo: nextDateTo });
      if (data.Order?.StoreSalesOrderId) navigate(`/store/orders/${data.Order.StoreSalesOrderId}`);
    } catch (error) {
      setMessageTone("error");
      setMessage(error instanceof Error ? error.message : "訂單建立失敗。");
    } finally {
      setSaving(false);
    }
  }

  async function handleVoid() {
    if (!order) return;
    if (!canVoidOrders) {
      setMessageTone("error");
      setMessage("此帳號沒有作廢門市訂單權限。");
      return;
    }
    setSaving(true);
    setMessage("");
    setMessageTone("error");
    try {
      const data = await voidStoreOrder(order.StoreSalesOrderId, voidReason);
      setOrder(data.Order);
      setVoidPanelOpen(false);
      setVoidReason("");
      await loadList(effectiveLocationId, 1);
    } catch (error) {
      setMessageTone("error");
      setMessage(error instanceof Error ? error.message : "訂單作廢失敗。");
    } finally {
      setSaving(false);
    }
  }

  function openPaymentPanel() {
    if (!order) return;
    setVoidPanelOpen(false);
    setPaymentPanelOpen(true);
    setPaymentAmount(Math.max(0, order.BalanceAmount || 0));
    setPaymentMethod("Cash");
    setPaymentNote("");
    setPaymentMessage("");
    setPaymentCompleteOrder(order.Status === "ShippedToStore" || order.Status === "ReadyForPickup" || order.FulfillmentType === "StoreStock");
    setDeliveryChecklist(deliveryChecklistFromOrder(order));
    window.setTimeout(() => {
      paymentPanelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 0);
  }

  async function handlePaymentSubmit() {
    if (!order) return;
    if (!canWriteOrders) {
      setPaymentMessage("此帳號沒有收款或結清門市訂單權限。");
      return;
    }
    setSaving(true);
    setMessage("");
    setMessageTone("error");
    setPaymentMessage("");
    try {
      const data = await createStoreOrderPayment(order.StoreSalesOrderId, {
        PaymentType: "Balance",
        PaymentMethod: paymentMethod,
        Amount: Math.max(0, Number(paymentAmount || 0)),
        Note: paymentNote,
        CompleteOrder: paymentIsZeroBalance ? orderCanCompleteDelivery : paymentCompleteOrder && paymentCanCompleteOrder,
        DeliveryChecklist: deliveryChecklist,
      });
      setOrder(data.Order);
      setPaymentPanelOpen(false);
      setPaymentNote("");
      setPaymentMessage("");
      await loadList(effectiveLocationId, pagination.Page);
    } catch (error) {
      setPaymentMessage(error instanceof Error ? error.message : "收款紀錄儲存失敗。");
    } finally {
      setSaving(false);
    }
  }

  async function handleSendPickupNotification() {
    if (!order) return;
    setSendingNotification(true);
    setMessage("");
    setMessageTone("error");
    try {
      const data = await sendStoreOrderPickupNotification(order.StoreSalesOrderId);
      await loadList(effectiveLocationId, pagination.Page);
      setOrder(data.Order);
      setMessageTone("success");
      setMessage(data.message || "取件通知已寄送。");
    } catch (error) {
      setMessageTone("error");
      setMessage(error instanceof Error ? error.message : "取件通知寄送失敗。");
    } finally {
      setSendingNotification(false);
    }
  }

  async function handleConfirmStoreReceipt() {
    if (!order) return;
    if (!canWriteOrders) {
      setMessageTone("error");
      setMessage("此帳號沒有確認收到貨權限。");
      return;
    }
    setSaving(true);
    setMessage("");
    setMessageTone("error");
    try {
      const data = await confirmStoreOrderReceipt(order.StoreSalesOrderId, { Note: receiptNote });
      await loadList(effectiveLocationId, pagination.Page);
      setOrder(data.Order);
      setReceiptNote("");
      setMessageTone("success");
      setMessage(data.message || "已確認收到貨。");
    } catch (error) {
      setMessageTone("error");
      setMessage(error instanceof Error ? error.message : "確認收到貨失敗。");
    } finally {
      setSaving(false);
    }
  }

  async function handleAddNotificationLog() {
    if (!order) return;
    if (!canWriteOrders) {
      setMessageTone("error");
      setMessage("此帳號沒有新增通知紀錄權限。");
      return;
    }
    if (notificationBody.trim() === "") {
      setMessageTone("error");
      setMessage("請輸入通知紀錄內容。");
      return;
    }
    setSaving(true);
    setMessage("");
    setMessageTone("error");
    try {
      const data = await addStoreOrderNotificationLog(order.StoreSalesOrderId, {
        Method: notificationMethod,
        Status: notificationStatus,
        BodySummary: notificationBody.trim(),
      });
      setOrder(data.Order);
      setNotificationBody("");
      setNotificationLogOpen(false);
      setMessageTone("success");
      setMessage(data.message || "通知紀錄已建立。");
      await loadList(effectiveLocationId, pagination.Page);
    } catch (error) {
      setMessageTone("error");
      setMessage(error instanceof Error ? error.message : "通知紀錄建立失敗。");
    } finally {
      setSaving(false);
    }
  }

  return (
    <StoreLayout currentLocationId={effectiveLocationId} onLocationChange={handleLocationChange} onLogout={handleLogout} title="門市訂單" user={user}>
      <div className="space-y-4">
        <form className="rounded-md border border-[#dfe6e1] bg-white p-3" onSubmit={handleSearch}>
          <div className="grid min-w-0 gap-3 sm:grid-cols-2 2xl:grid-cols-[minmax(300px,1fr)_minmax(170px,190px)_minmax(170px,190px)_minmax(180px,200px)_minmax(180px,200px)]">
            <div className="min-w-0 sm:col-span-2 2xl:col-span-1">
              <Input label="關鍵字" placeholder="單號、姓名、電話、Email" value={keyword} onChange={setKeyword} />
            </div>
            <Select
              label="流程狀態"
              options={[
                { Key: "", Label: "進行中狀態" },
                { Key: "All", Label: "全部訂單" },
                { Key: "EngineeringOrPurchase", Label: "待工程部/採購" },
                { Key: "Deliverable", Label: "可交付流程" },
                ...statusOptions,
              ]}
              value={status}
              onChange={setStatus}
            />
            <Select label="付款狀態" options={[{ Key: "", Label: "全部付款" }, ...paymentOptions]} value={paymentStatus} onChange={setPaymentStatus} />
            <Input label="起日" type="date" value={dateFrom} onChange={setDateFrom} />
            <Input label="迄日" type="date" value={dateTo} onChange={setDateTo} />
          </div>
          <div className="rwd-action-grid mt-3">
            <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md bg-[#315746] px-5 font-bold text-white" type="submit"><Search size={18} />查詢</button>
            <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746] hover:bg-[#f0f5f2]" onClick={resetFilters} type="button"><RefreshCw size={18} />重設篩選</button>
          </div>
        </form>

        {message && (
          <div className={`rounded-md border px-4 py-3 text-sm font-bold ${messageTone === "success" ? "border-[#b9ddc5] bg-[#eef8f1] text-[#245438]" : "border-[#f2b8b5] bg-[#fdebea] text-[#8a1f1f]"}`}>
            {message}
          </div>
        )}

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-6">
          <StatusFilterCard active={status === "" && paymentStatus === ""} label="進行中訂單" value={`${Math.max(0, (counts.Total || 0) - (counts.Completed || 0) - (counts.Voided || 0) - (counts.Cancelled || 0))} 筆`} onClick={() => applyStatusFilter("")} />
          <StatusFilterCard active={status === "All"} label="全部訂單" value={`${counts.Total || 0} 筆`} onClick={() => applyStatusFilter("All")} />
          <StatusFilterCard active={status === "PendingHeadquarters"} label="待總部確認" value={`${counts.PendingHeadquarters || 0} 筆`} onClick={() => applyStatusFilter("PendingHeadquarters")} />
          <StatusFilterCard active={status === "EngineeringOrPurchase"} label="待工程部/採購" value={`${(counts.PendingEngineering || 0) + (counts.WaitingPurchase || 0)} 筆`} onClick={() => applyStatusFilter("EngineeringOrPurchase")} />
          <StatusFilterCard active={status === "Deliverable"} label="可交付流程" value={`${(counts.ShippedToStore || 0) + (counts.ReadyForPickup || 0)} 筆`} onClick={() => applyStatusFilter("Deliverable")} />
          <StatusFilterCard active={paymentStatus === "BalanceDue"} label="尾款待收" value={`${counts.BalanceDue || 0} 筆`} onClick={() => applyPaymentFilter("BalanceDue")} />
        </div>

        <div className="grid gap-4 2xl:grid-cols-[minmax(320px,430px)_minmax(0,1fr)]">
          <section className="min-w-0 rounded-md border border-[#dfe6e1] bg-white p-3">
            <div className="mb-3 flex items-center justify-between px-1">
              <div>
                <h2 className="font-extrabold text-[#203d32]">訂單列表</h2>
                <p className="mt-1 text-sm text-[#7b877f]">共 {pagination.Total} 筆</p>
              </div>
              <ShoppingBag className="text-[#315746]" size={20} />
            </div>
            {loading ? (
              <EmptyText text="載入中..." />
            ) : orders.length === 0 ? (
              <EmptyText text="目前沒有符合條件的門市訂單。" />
            ) : (
              <div className="grid gap-3">
                {orders.map((item) => (
                  <OrderCard
                    item={item}
                    key={item.StoreSalesOrderId}
                    selected={item.StoreSalesOrderId === storeSalesOrderId}
                    onSelect={() => {
                      if (!editingOrderId) setFormOpen(false);
                      navigate(`/store/orders/${item.StoreSalesOrderId}`);
                    }}
                  />
                ))}
              </div>
            )}
            <div className="mt-4 flex flex-col gap-2 border-t border-[#edf0ee] pt-3 sm:flex-row sm:items-center sm:justify-between">
              <button className="h-10 rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746] disabled:opacity-50" disabled={pagination.Page <= 1 || loading} onClick={() => loadList(effectiveLocationId, pagination.Page - 1)} type="button">上一頁</button>
              <span className="text-center text-sm font-bold text-[#59665f]">{pagination.Page} / {pagination.TotalPages}</span>
              <button className="h-10 rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746] disabled:opacity-50" disabled={pagination.Page >= pagination.TotalPages || loading} onClick={() => loadList(effectiveLocationId, pagination.Page + 1)} type="button">下一頁</button>
            </div>
          </section>

          <section className="min-w-0 space-y-4">
            {!formOpen ? (
              <div className="flex flex-col gap-3 rounded-md border border-[#dfe6e1] bg-white p-4 md:flex-row md:items-center md:justify-between">
                <div>
                  <h2 className="flex items-center gap-2 font-extrabold text-[#203d32]"><FilePlus2 size={18} />門市訂單</h2>
                  <p className="mt-1 text-sm font-medium text-[#7b877f]">選擇左側訂單查看流程；需要建立新單時再展開表單。</p>
                </div>
                {canWriteOrders ? (
                  <button className="inline-flex h-11 w-full shrink-0 items-center justify-center gap-2 rounded-md bg-[#315746] px-5 font-bold text-white hover:bg-[#203d32] md:w-auto" onClick={startCreate} type="button">
                    <FilePlus2 size={18} />新增訂單
                  </button>
                ) : (
                  <span className="rounded-md border border-[#dfe6e1] bg-[#fbfcfb] px-4 py-3 text-sm font-bold text-[#7b877f]">僅可查看訂單</span>
                )}
              </div>
            ) : (
            <form className="rounded-md border border-[#dfe6e1] bg-white p-4" onSubmit={handleSubmitOrder}>
              <div className="mb-4 flex flex-col gap-3 border-b border-[#edf0ee] pb-4 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <h2 className="flex items-center gap-2 font-extrabold text-[#203d32]"><FilePlus2 size={18} />{editingOrderId ? "編輯門市訂單" : "新增門市訂單"}</h2>
                  <p className="mt-1 text-sm font-medium text-[#7b877f]">{form.FulfillmentType === "StoreStock" ? "門市現貨交付會直接完成交付紀錄，但不扣官網庫存。" : "送出後進入「待總部確認」，門市端不扣庫存。"}</p>
                </div>
                <div className="rwd-action-grid">
                  <button className="inline-flex h-10 min-w-[116px] items-center justify-center gap-2 whitespace-nowrap rounded-md border border-[#cfd8d2] bg-white px-4 text-sm font-bold text-[#315746] hover:bg-[#f0f5f2]" onClick={cancelEdit} type="button">
                    <X size={16} />{editingOrderId ? "取消編輯" : "取消新增"}
                  </button>
                  <button className="inline-flex h-10 min-w-[116px] items-center justify-center gap-2 whitespace-nowrap rounded-md bg-[#315746] px-4 text-sm font-bold text-white disabled:opacity-60" disabled={saving || draftItems.length === 0 || !canWriteOrders || (requiresZeroPriceAudit && !zeroPriceAuditReady)} type="submit"><Save size={16} />{saving ? "儲存中" : editingOrderId ? "儲存修改" : "送出訂單"}</button>
                </div>
              </div>

              <div className="grid gap-3 xl:grid-cols-[minmax(0,1fr)_190px_190px_220px]">
                <div className="relative">
                  <div className="grid gap-3 md:grid-cols-3">
                    <Input label="客戶姓名" value={form.CustomerName} onChange={(value) => updateCustomerField("CustomerName", value)} />
                    <Input label="客戶電話" value={form.CustomerPhone} onChange={(value) => updateCustomerField("CustomerPhone", value)} />
                    <Input label="Email" value={form.CustomerEmail || ""} onChange={(value) => setForm((current) => ({ ...current, CustomerEmail: value }))} />
                  </div>
                  {(customerOptions.length > 0 || customerSearching) && (
                    <div className="absolute left-0 right-0 top-[72px] z-20 rounded-md border border-[#dfe6e1] bg-white p-2 shadow-[0_16px_36px_rgba(32,61,50,.14)]">
                      {customerSearching && <div className="px-3 py-2 text-sm font-bold text-[#7b877f]">搜尋客戶中...</div>}
                      {customerOptions.map((item) => (
                        <button className="flex w-full items-center justify-between gap-3 rounded px-3 py-2 text-left hover:bg-[#f0f5f2]" key={item.CustomerKey} onClick={() => selectCustomer(item)} type="button">
                          <span className="min-w-0">
                            <span className="block truncate text-sm font-extrabold text-[#203d32]">{item.Name}</span>
                            <span className="block truncate text-xs font-bold text-[#7b877f]">{item.Phone}{item.Email ? ` / ${item.Email}` : ""}</span>
                          </span>
                          <span className={`shrink-0 rounded-full px-2 py-1 text-xs font-bold ${item.IsMember ? "bg-[#fff2d8] text-[#8a4c00]" : "bg-[#e9f4ec] text-[#245438]"}`}>
                            {item.IsMember ? "系統會員" : `${item.OrderCount} 訂單`}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <Select label="訂單性質" options={orderPurposeOptions} value={form.OrderPurpose || "Sale"} onChange={(value) => setForm((current) => ({ ...current, OrderPurpose: value }))} />
                <div>
                  <label className="mb-1 block text-sm font-bold text-[#59665f]">付款狀態</label>
                  <div className="flex h-11 items-center rounded-md border border-[#dfe6e1] bg-[#f4f6f5] px-3 text-sm font-extrabold text-[#203d32]">{draftPaymentStatusLabel}</div>
                </div>
                <Input label="預計取件日" type="date" value={form.ExpectedPickupDate || ""} onChange={(value) => setForm((current) => ({ ...current, ExpectedPickupDate: value }))} />
              </div>
              {(requiresZeroPriceAudit || form.OrderPurpose !== "Sale") && (
                <div className={`mt-3 rounded-md border p-3 ${zeroPriceAuditReady ? "border-[#bcdcc6] bg-[#f2fbf5]" : "border-[#f2d29b] bg-[#fff9ec]"}`}>
                  <div className="mb-3 flex flex-col gap-1 md:flex-row md:items-center md:justify-between">
                    <div>
                      <p className="text-sm font-extrabold text-[#203d32]">0 元控管與稽核</p>
                      <p className="text-xs font-bold leading-5 text-[#7b877f]">試戴、保固、贈品、補件或測試單可用 0 元，但必須留下原因，方便總管理端查核。</p>
                    </div>
                    {requiresZeroPriceAudit && !zeroPriceAuditReady && <span className="rounded-full bg-[#fff2d8] px-3 py-1 text-xs font-extrabold text-[#8a4c00]">尚未完成</span>}
                  </div>
                  <div className="grid gap-3 lg:grid-cols-[240px_minmax(0,1fr)]">
                    <Select
                      label="0 元原因"
                      options={[{ Key: "", Label: "請選擇原因" }, ...zeroPriceReasonOptions]}
                      value={form.ZeroPriceReason || ""}
                      onChange={(value) => setForm((current) => {
                        const matchedPurpose = purposeFromZeroPriceReason(value);
                        return {
                          ...current,
                          ZeroPriceReason: value,
                          OrderPurpose: matchedPurpose || current.OrderPurpose,
                        };
                      })}
                    />
                    <Input label="0 元備註" placeholder="例如：試戴借出 7 天、主管核准折讓、保固更換原因" value={form.ZeroPriceNote || ""} onChange={(value) => setForm((current) => ({ ...current, ZeroPriceNote: value }))} />
                  </div>
                  {requiresZeroPriceAudit && zeroPriceAuditMissingText && (
                    <p className="mt-2 text-xs font-extrabold leading-5 text-[#9a5b00]">{zeroPriceAuditMissingText}</p>
                  )}
                  {zeroPriceBlockedItems.length > 0 && (
                    <p className="mt-2 text-xs font-extrabold leading-5 text-[#9a5b00]">一般銷售商品不可直接 0 元：{zeroPriceBlockedItems.map((item) => item.DisplayName).join("、")}。請改選正式訂單性質，或先由總管理端設定商品允許 0 元。</p>
                  )}
                </div>
              )}
              <div className="mt-3 grid gap-3 lg:grid-cols-[220px_220px_minmax(0,1fr)]">
                <Select label="交付方式" options={fulfillmentOptions} value={form.FulfillmentType || "Headquarters"} onChange={(value) => setForm((current) => ({ ...current, FulfillmentType: value, DeliveryCompleted: value === "StoreStock", DeliveryNote: value === "StoreStock" ? current.DeliveryNote : "" }))} />
                <DeliveryCompletedField
                  checked={Boolean(form.DeliveryCompleted)}
                  disabled={form.FulfillmentType !== "StoreStock"}
                  onChange={(checked) => setForm((current) => ({ ...current, DeliveryCompleted: checked }))}
                />
                <Input label="交付備註" value={form.DeliveryNote || ""} onChange={(value) => setForm((current) => ({ ...current, DeliveryNote: value }))} />
              </div>

              <div className="mt-4 rounded-md border border-[#edf0ee] bg-[#fbfcfb] p-3">
                <label className="mb-1 block text-sm font-bold text-[#59665f]">商品規格</label>
                <div className="relative">
                  <div className="flex flex-col gap-2 md:flex-row">
                    <div className="relative flex-1">
                      <PackageSearch className="pointer-events-none absolute left-3 top-3 text-[#7b877f]" size={18} />
                      <input className="h-11 w-full rounded-md border border-[#cfd8d2] pl-10 pr-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" placeholder="輸入商品名稱、SKU、規格模糊查詢" value={productKeyword} onChange={(event) => setProductKeyword(event.target.value)} />
                    </div>
                  </div>
                  {(productOptions.length > 0 || productSearching) && (
                    <div className="absolute left-0 right-0 top-[48px] z-20 max-h-[280px] overflow-y-auto rounded-md border border-[#dfe6e1] bg-white p-2 shadow-[0_16px_36px_rgba(32,61,50,.14)]">
                      {productSearching && <div className="px-3 py-2 text-sm font-bold text-[#7b877f]">搜尋商品中...</div>}
                      {productOptions.map((item) => (
                        <button className="w-full rounded px-3 py-2 text-left hover:bg-[#f0f5f2]" key={`${item.ProductId}-${item.VariantId || "base"}`} onClick={() => addProduct(item)} type="button">
                          <span className="block truncate text-sm font-extrabold text-[#203d32]">{item.DisplayName}</span>
                          <span className="mt-1 flex flex-wrap gap-2 text-xs font-bold text-[#7b877f]">
                            <span>{item.Sku}</span>
                            <span>{money(item.Price)}</span>
                            <span>庫存參考 {item.Stock ?? "-"}</span>
                            <span className="rounded-full bg-[#eef5f1] px-2 py-0.5 text-[#315746]">{item.ProductUsageTypeLabel || "一般銷售"}</span>
                            {item.AllowZeroPriceOrder && <span className="rounded-full bg-[#fff2d8] px-2 py-0.5 text-[#8a4c00]">可 0 元</span>}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <p className="mt-2 text-xs font-bold text-[#7b877f]">庫存只作為總部備貨參考；門市訂單送出不會扣庫存。</p>

                <div className="mt-3 grid gap-3">
                  {draftItems.length === 0 ? (
                    <div className="rounded-md border border-dashed border-[#cfd8d2] bg-white px-4 py-5 text-center text-sm font-medium text-[#7b877f]">尚未加入商品規格。</div>
                  ) : draftItems.map((item, index) => (
                    <div className="rounded-md border border-[#dfe6e1] bg-white p-3" key={`${item.ProductId}-${item.VariantId}-${index}`}>
                      <div className="flex flex-col gap-2 border-b border-[#edf0ee] pb-3 md:flex-row md:items-start md:justify-between">
                        <div className="min-w-0">
                          <p className="break-words text-base font-extrabold leading-6 text-[#203d32]">{item.DisplayName}</p>
                          <div className="mt-2 flex flex-wrap gap-2 text-xs font-bold text-[#7b877f]">
                            <span className="rounded bg-[#f4f6f5] px-2 py-1">SKU {item.Sku || "-"}</span>
                            <span className="rounded bg-[#f4f6f5] px-2 py-1">原價 {money(item.UnitPrice)}</span>
                            <span className="rounded bg-[#f4f6f5] px-2 py-1">庫存參考 {item.Stock ?? "-"}</span>
                            <span className="rounded bg-[#eef5f1] px-2 py-1">{item.ProductUsageTypeLabel || "一般銷售"}</span>
                            {item.AllowZeroPriceOrder && <span className="rounded bg-[#fff2d8] px-2 py-1 text-[#8a4c00]">可 0 元</span>}
                            {item.DealPrice === 0 && !item.AllowZeroPriceOrder && <span className="rounded bg-[#fdebea] px-2 py-1 text-[#9a2d2d]">需原因</span>}
                          </div>
                        </div>
                        <div className="flex shrink-0 items-center justify-between gap-3 md:justify-end">
                          <div className="text-right">
                            <p className="text-xs font-bold text-[#7b877f]">小計</p>
                            <p className="text-lg font-extrabold text-[#203d32]">{money(item.DealPrice * item.Quantity)}</p>
                          </div>
                          <button className="h-10 rounded-md border border-[#f2c9c6] px-3 text-xs font-bold text-[#9a2d2d] hover:bg-[#fdebea]" onClick={() => removeDraftItem(index)} type="button">移除</button>
                        </div>
                      </div>
                      <div className="mt-3 grid gap-3 md:grid-cols-2 2xl:grid-cols-[150px_110px_minmax(180px,1fr)_150px_170px]">
                        <CompactField label="成交價"><NumberInput fluid value={item.DealPrice} onChange={(value) => updateDraftItem(index, "DealPrice", value)} /></CompactField>
                        <CompactField label="數量"><NumberInput compact fluid value={item.Quantity} onChange={(value) => updateDraftItem(index, "Quantity", value)} /></CompactField>
                        <CompactField label="機身序號"><SmallInput fluid placeholder="機身序號" value={item.SerialNumber} onChange={(value) => updateDraftItemText(index, "SerialNumber", value)} /></CompactField>
                        <CompactField label="耳側">
                          <select className="h-10 w-full rounded-md border border-[#cfd8d2] bg-white px-2 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={item.EarSide} onChange={(event) => updateDraftItemText(index, "EarSide", event.target.value)}>
                            <option value="">未指定</option>
                            <option value="Left">左耳</option>
                            <option value="Right">右耳</option>
                            <option value="Both">雙耳</option>
                            <option value="Accessory">配件</option>
                          </select>
                        </CompactField>
                        <CompactField label="保固起算"><SmallInput fluid type="date" value={item.WarrantyStartDate || (form.FulfillmentType === "StoreStock" ? today() : "")} onChange={(value) => updateDraftItemText(index, "WarrantyStartDate", value)} /></CompactField>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-4 grid gap-3 lg:grid-cols-[1fr_1fr]">
                <TextArea label="客戶備註" placeholder="例如：客戶希望到貨後下午聯絡、偏好右耳先交付。" value={form.CustomerNote || ""} onChange={(value) => setForm((current) => ({ ...current, CustomerNote: value }))} />
                <TextArea label="內部備註" placeholder="例如：已與客戶確認價格，需總部協助確認規格供貨。" value={form.InternalNote || ""} onChange={(value) => setForm((current) => ({ ...current, InternalNote: value }))} />
              </div>

              <div className="mt-4 grid gap-3 2xl:grid-cols-[minmax(0,1fr)_minmax(300px,360px)]">
                <div className="rounded-md border border-[#dfe6e1] bg-white p-4">
                  <div className="mb-3 flex items-center gap-2">
                    <WalletCards size={18} className="text-[#315746]" />
                    <h3 className="font-extrabold text-[#203d32]">首筆收款</h3>
                  </div>
                  <div className="grid gap-3 md:grid-cols-[minmax(140px,180px)_minmax(150px,190px)_1fr]">
                    <div>
                      <label className="mb-1 block text-sm font-bold text-[#59665f]">本次實收</label>
                      <input className="h-11 w-full rounded-md border border-[#cfd8d2] px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" min={0} type="number" value={initialPaymentAmount} onChange={(event) => setForm((current) => ({ ...current, DepositAmount: Number(event.target.value || 0), InitialPaymentAmount: Number(event.target.value || 0) }))} />
                    </div>
                    <Select label="收款方式" options={paymentMethods} value={form.InitialPaymentMethod || "Cash"} onChange={(value) => setForm((current) => ({ ...current, InitialPaymentMethod: value }))} />
                    <Input label="收款備註" placeholder="例如：信用卡授權碼、匯款末五碼" value={form.InitialPaymentNote || ""} onChange={(value) => setForm((current) => ({ ...current, InitialPaymentNote: value }))} />
                  </div>
                  <p className="mt-2 text-xs font-bold leading-5 text-[#7b877f]">送出訂單時會建立第一筆收款流水；後續尾款或調整請從訂單詳情的收款功能新增。</p>
                </div>
                <div className="rounded-md border border-[#dfe6e1] bg-white p-4">
                  <div className="grid gap-3">
                    <InfoTile label="訂單總額" value={money(totalAmount)} tone="strong" />
                    <InfoTile label="本次已收" value={money(initialPaymentAmount)} />
                    <InfoTile label="尾款" value={money(balanceAmount)} />
                  </div>
                </div>
              </div>

              <div className="mt-4 rounded-md border border-[#dfe6e1] bg-white p-4">
                <div className="mb-3 flex items-center gap-2">
                  <ReceiptText size={18} className="text-[#315746]" />
                  <h3 className="font-extrabold text-[#203d32]">發票資訊</h3>
                </div>
                <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                  <Select label="發票類型" options={invoiceTypes} value={form.InvoiceType || "Pending"} onChange={(value) => setForm((current) => ({ ...current, InvoiceType: value }))} />
                  <Input label="統一編號" placeholder="三聯式需填 8 碼" value={form.InvoiceTaxId || ""} onChange={(value) => setForm((current) => ({ ...current, InvoiceTaxId: value }))} />
                  <Input label="發票抬頭" value={form.InvoiceTitle || ""} onChange={(value) => setForm((current) => ({ ...current, InvoiceTitle: value }))} />
                  <Input label="載具號碼" value={form.InvoiceCarrier || ""} onChange={(value) => setForm((current) => ({ ...current, InvoiceCarrier: value }))} />
                  <Input label="捐贈碼" value={form.InvoiceDonationCode || ""} onChange={(value) => setForm((current) => ({ ...current, InvoiceDonationCode: value }))} />
                  <div className="md:col-span-2 xl:col-span-3">
                    <Input label="發票備註" placeholder="例如：客戶稍後補統編、需公司抬頭確認" value={form.InvoiceNote || ""} onChange={(value) => setForm((current) => ({ ...current, InvoiceNote: value }))} />
                  </div>
                </div>
              </div>
            </form>
            )}

            <section className="rounded-md border border-[#dfe6e1] bg-white p-4">
              {!storeSalesOrderId ? (
                <div className="flex min-h-[260px] flex-col items-center justify-center rounded-md border border-dashed border-[#cfd8d2] bg-[#f7faf8] px-4 text-center">
                  <ClipboardCheck className="mb-3 text-[#315746]" size={34} />
                  <h2 className="text-xl font-extrabold text-[#203d32]">選擇一筆訂單查看流程狀態</h2>
                </div>
              ) : detailLoading ? (
                <EmptyText text="詳情載入中..." />
              ) : order ? (
                <div className="space-y-4">
                  <div className="flex flex-col gap-3 border-b border-[#edf0ee] pb-4 md:flex-row md:items-start md:justify-between">
                    <div className="min-w-0">
                      <div className="mb-2 flex flex-wrap items-center gap-2">
                        <span className="rounded-full bg-[#e9f4ec] px-2.5 py-1 text-xs font-bold text-[#245438]">{order.StatusLabel || order.Status}</span>
                        <span className="rounded-full bg-[#f4f6f5] px-2.5 py-1 text-xs font-bold text-[#59665f]">{order.PaymentStatusLabel || order.PaymentStatus}</span>
                      </div>
                      <h2 className="break-all text-2xl font-extrabold text-[#203d32]">{order.OrderNumber}</h2>
                      <p className="mt-1 text-sm text-[#59665f]">{order.CustomerName} / {order.CustomerPhone}</p>
                    </div>
                    <div className="rwd-action-grid">
                      <button className="inline-flex h-11 shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-[#315746] px-5 font-bold text-white" onClick={() => copyPhoneNumber(order.CustomerPhone)} type="button"><Copy size={18} />複製電話</button>
                      <button className="inline-flex h-11 min-w-[132px] shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746]" onClick={() => printDocument(`訂購確認單-${order.OrderNumber}`, orderPrintHtml(order))} type="button"><Printer size={18} />訂購確認單</button>
                      {order.Status === "PendingHeadquarters" && (canWriteOrders || canVoidOrders) && (
                        <>
                          {canWriteOrders && <button className="inline-flex h-11 shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746]" onClick={() => startEdit(order)} type="button"><Pencil size={18} />編輯</button>}
                          {canVoidOrders && <button className="inline-flex h-11 shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md border border-[#f2c9c6] bg-white px-4 font-bold text-[#9a2d2d]" onClick={() => { setVoidPanelOpen(true); setVoidReason(""); }} type="button"><Ban size={18} />作廢</button>}
                        </>
                      )}
                    </div>
                  </div>
                  {voidPanelOpen && (
                    <div className="rounded-md border border-[#f2c9c6] bg-[#fff8f7] p-4">
                      <div className="mb-3 flex items-center justify-between gap-3">
                        <h3 className="font-extrabold text-[#9a2d2d]">作廢訂單</h3>
                        <button className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-[#e8c8c5] bg-white text-[#9a2d2d]" onClick={() => setVoidPanelOpen(false)} type="button"><X size={16} /></button>
                      </div>
                      <TextArea label="作廢原因" placeholder="例如：建錯客戶、重複建立、客戶取消。" value={voidReason} onChange={setVoidReason} />
                      <div className="mt-3 flex justify-end">
                        <button className="inline-flex h-10 items-center justify-center gap-2 rounded-md bg-[#9a2d2d] px-4 text-sm font-bold text-white disabled:opacity-60" disabled={saving || voidReason.trim() === ""} onClick={handleVoid} type="button"><Ban size={16} />確認作廢</button>
                      </div>
                    </div>
                  )}
                  {paymentPanelOpen && order && (
                    <div ref={paymentPanelRef} className="scroll-mt-4 rounded-md border border-[#9fc7ad] bg-[#f4fbf6] p-4 shadow-[0_10px_28px_rgba(49,87,70,.08)]">
                      <div className="mb-3 flex items-center justify-between gap-3">
                        <div>
                          <h3 className="font-extrabold text-[#203d32]">{paymentIsZeroBalance ? "完成交付結單" : "收尾款 / 結清"}</h3>
                          <p className="mt-1 text-sm font-medium text-[#59665f]">
                            {paymentIsZeroBalance ? "尾款已結清，確認商品交付後即可完成訂單。" : "收款會建立獨立流水，日後可追溯收款人與時間。"}
                          </p>
                        </div>
                        <button className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-[#cfd8d2] bg-white text-[#315746]" onClick={() => setPaymentPanelOpen(false)} type="button"><X size={16} /></button>
                      </div>
                      {paymentIsZeroBalance ? (
                        <div className="rounded-md border border-[#dfe6e1] bg-white px-4 py-3 text-sm font-bold leading-6 text-[#315746]">
                          此訂單尾款已為 NT$ 0，本次不會新增收款金額；系統只會留下交付結單時間與操作人員。
                        </div>
                      ) : (
                        <div className="grid gap-3 md:grid-cols-[minmax(130px,180px)_minmax(150px,190px)_1fr]">
                          <div>
                            <label className="mb-1 block text-sm font-bold text-[#59665f]">本次實收</label>
                            <input className="h-11 w-full rounded-md border border-[#cfd8d2] px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" min={0} max={order.BalanceAmount || 0} type="number" value={paymentAmount} onChange={(event) => setPaymentAmount(Number(event.target.value || 0))} />
                          </div>
                          <Select label="收款方式" options={paymentMethods} value={paymentMethod} onChange={setPaymentMethod} />
                          <Input label="收款備註" placeholder="例如：尾款現金結清、匯款末五碼" value={paymentNote} onChange={setPaymentNote} />
                        </div>
                      )}
                      {paymentMessage && (
                        <div className="mt-3 rounded-md border border-[#f0d0a6] bg-[#fff8ed] px-3 py-2 text-sm font-bold text-[#8a5517]">{paymentMessage}</div>
                      )}
                      {orderCanCompleteDelivery && (paymentIsZeroBalance || paymentCanCompleteOrder) && (
                        <div className="mt-3 rounded-md border border-[#dfe6e1] bg-white p-3">
                          <div className="mb-2 flex items-center gap-2">
                            <ListChecks size={17} className="text-[#315746]" />
                            <h4 className="font-extrabold text-[#203d32]">交付前檢核</h4>
                          </div>
                          <p className="mb-3 text-xs font-bold leading-5 text-[#7b877f]">依本次商品與現場流程勾選適用項目；未勾選項目會留空紀錄，不會影響付款儲存。</p>
                          <div className="grid gap-2 sm:grid-cols-2">
                            {deliveryChecklistKeys.map((key) => (
                              <label className="flex min-h-11 items-center gap-3 rounded-md border border-[#edf0ee] bg-[#fbfcfb] px-3 py-2 text-sm font-bold leading-5 text-[#315746]" key={key}>
                                <input
                                  checked={Boolean(deliveryChecklist[key])}
                                  className="h-4 w-4 shrink-0 accent-[#315746]"
                                  type="checkbox"
                                  onChange={(event) => setDeliveryChecklist((current) => ({ ...current, [key]: event.target.checked }))}
                                />
                                <span>{order.DeliveryChecklist?.[key]?.Label || fallbackDeliveryChecklistLabels[key]}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      )}
                      {!paymentIsZeroBalance && (
                        <label className={`mt-3 flex min-h-11 items-center gap-3 rounded-md border px-3 py-2 text-sm font-bold leading-none ${paymentCanCompleteOrder ? "border-[#dfe6e1] bg-white text-[#315746]" : "border-[#dfe6e1] bg-[#f4f6f5] text-[#9aa8a0]"}`}>
                          <input
                            checked={paymentCompleteOrder && paymentCanCompleteOrder}
                            className="h-4 w-4 shrink-0 accent-[#315746]"
                            disabled={!paymentCanCompleteOrder}
                            type="checkbox"
                            onChange={(event) => setPaymentCompleteOrder(event.target.checked)}
                          />
                          <span className="leading-5">收款後同時完成交付結單</span>
                        </label>
                      )}
                      {!orderCanCompleteDelivery && (
                        <p className="mt-2 text-xs font-bold text-[#7b877f]">目前商品尚未到店，這次只會建立收款紀錄；到店或可取件後才能完成交付結單。</p>
                      )}
                      {orderCanCompleteDelivery && !paymentWillSettleBalance && (
                        <p className="mt-2 text-xs font-bold text-[#7b877f]">本次收款後仍有尾款，需尾款結清後才能同時完成交付結單。</p>
                      )}
                      <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:justify-end">
                        <button className="inline-flex h-10 items-center justify-center rounded-md border border-[#cfd8d2] bg-white px-4 text-sm font-bold text-[#315746]" onClick={() => setPaymentPanelOpen(false)} type="button">取消</button>
                        <button
                          className="inline-flex h-10 shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-[#315746] px-4 text-sm font-bold text-white disabled:opacity-60"
                          disabled={saving || (paymentIsZeroBalance ? !orderCanCompleteDelivery : (paymentAmount <= 0 && order.BalanceAmount > 0))}
                          onClick={handlePaymentSubmit}
                          type="button"
                        >
                          <WalletCards size={16} />{paymentIsZeroBalance ? "完成結單" : "儲存收款"}
                        </button>
                      </div>
                    </div>
                  )}
                  {canWriteOrders && order.Status !== "Voided" && order.Status !== "Cancelled" && order.Status !== "Completed" && (
                    <section className={`rounded-md border p-4 shadow-[0_10px_28px_rgba(32,61,50,.05)] ${(order.BalanceAmount || 0) > 0 ? "border-[#ead8ad] bg-[#fffaf0]" : "border-[#cfe3d5] bg-[#f6fbf7]"}`}>
                      <div className="flex flex-col gap-4 2xl:flex-row 2xl:items-center 2xl:justify-between">
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className={`inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md ${(order.BalanceAmount || 0) > 0 ? "bg-[#fff1cf] text-[#9a5b15]" : "bg-[#e3f2e8] text-[#245438]"}`}>
                              <WalletCards size={20} />
                            </span>
                            <div className="min-w-0">
                              <h3 className="text-lg font-extrabold text-[#203d32]">{(order.BalanceAmount || 0) > 0 ? "收款與剩餘待付金額" : "收款已結清"}</h3>
                              <p className="mt-1 text-sm font-semibold leading-6 text-[#59665f]">
                                {(order.BalanceAmount || 0) > 0 ? "請於客戶取件或交付前確認剩餘待付金額、收款方式與收款備註。" : "此訂單已無剩餘待付金額，商品到店或現貨交付後可完成交付結單。"}
                              </p>
                            </div>
                          </div>
                        </div>
                        <button className="inline-flex min-h-12 w-full shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-[#315746] px-5 text-base font-extrabold text-white shadow-[0_8px_18px_rgba(49,87,70,.18)] 2xl:w-auto 2xl:min-w-[156px]" onClick={openPaymentPanel} type="button">
                          <WalletCards size={18} />{(order.BalanceAmount || 0) === 0 ? "完成交付" : "收尾款"}
                        </button>
                      </div>
                      <div className="mt-4 grid gap-3 sm:grid-cols-3">
                        <div className="rounded-md border border-white/70 bg-white px-4 py-3">
                          <p className="text-xs font-extrabold text-[#7b877f]">訂單總額</p>
                          <p className="mt-1 break-words text-xl font-extrabold text-[#203d32]">{money(order.TotalAmount)}</p>
                        </div>
                        <div className="rounded-md border border-white/70 bg-white px-4 py-3">
                          <p className="text-xs font-extrabold text-[#7b877f]">已收金額</p>
                          <p className="mt-1 break-words text-xl font-extrabold text-[#203d32]">{money(order.DepositAmount)}</p>
                        </div>
                        <div className={`rounded-md border px-4 py-3 ${(order.BalanceAmount || 0) > 0 ? "border-[#f0d49d] bg-white" : "border-[#cfe3d5] bg-white"}`}>
                          <p className="text-xs font-extrabold text-[#7b877f]">剩餘待付金額</p>
                          <p className={`mt-1 break-words text-2xl font-extrabold ${(order.BalanceAmount || 0) > 0 ? "text-[#9a5b15]" : "text-[#245438]"}`}>{money(order.BalanceAmount)}</p>
                        </div>
                      </div>
                    </section>
                  )}
                  <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                    <InfoTile label="訂單總額" value={money(order.TotalAmount)} tone="strong" />
                    <InfoTile label="已收金額" value={money(order.DepositAmount)} />
                    <InfoTile label="剩餘待付金額" value={money(order.BalanceAmount)} />
                    <InfoTile label="預計取件日" value={order.ExpectedPickupDate} />
                    <InfoTile label="交付方式" value={order.FulfillmentTypeLabel} />
                    <InfoTile label="交付時間" value={order.DeliveredAt} />
                    <InfoTile label="成交門市" value={order.LocationName} />
                    <InfoTile label="建立同仁" value={order.StoreUserName} />
                    <InfoTile label="Email" value={order.CustomerEmail} />
                    <InfoTile label="建立時間" value={order.CreatedAt} />
                    <InfoTile label="訂單性質" value={order.OrderPurposeLabel || order.OrderPurpose || "一般銷售"} />
                    {(order.ZeroPriceReason || order.ZeroPriceNote) && (
                      <InfoTile label="0 元原因" value={[order.ZeroPriceReasonLabel || order.ZeroPriceReason, order.ZeroPriceNote].filter(Boolean).join(" / ")} />
                    )}
                  </div>
                  {order.Status === "ShippedToStore" && canWriteOrders && (
                    <div className="rounded-md border border-[#cfe3d5] bg-[#f6fbf7] p-4">
                      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                        <div className="min-w-0">
                          <h3 className="font-extrabold text-[#203d32]">門市到貨簽收</h3>
                          <p className="mt-1 text-sm font-medium leading-6 text-[#59665f]">ERP 已出庫到門市；請確認實際收到商品與外箱狀態後簽收，簽收後訂單會進入可通知取件。</p>
                        </div>
                        <button className="inline-flex h-11 shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-[#315746] px-5 font-bold text-white disabled:opacity-60" disabled={saving} onClick={handleConfirmStoreReceipt} type="button">
                          <ClipboardCheck size={18} />確認收到貨
                        </button>
                      </div>
                      <textarea className="mt-3 min-h-[78px] w-full rounded-md border border-[#cfd8d2] px-3 py-2 text-sm outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" maxLength={500} placeholder="簽收備註，例如：外箱完整、已核對品名與數量。" value={receiptNote} onChange={(event) => setReceiptNote(event.target.value)} />
                    </div>
                  )}
                  {order.StoreReceivedAt && (
                    <div className="rounded-md border border-[#edf0ee] bg-white p-4 text-sm font-bold text-[#59665f]">
                      門市已於 {order.StoreReceivedAt} 確認收到貨{order.StoreReceivedNote ? `：${order.StoreReceivedNote}` : "。"}
                    </div>
                  )}
                  <div className="grid gap-3 rounded-md border border-[#dfe6e1] bg-white p-4 md:grid-cols-2 xl:grid-cols-4">
                    <InfoTile label="發票類型" value={order.InvoiceTypeLabel || order.InvoiceType} />
                    <InfoTile label="發票狀態" value={order.InvoiceStatusLabel || order.InvoiceStatus} />
                    <InfoTile label="統一編號" value={order.InvoiceTaxId} />
                    <InfoTile label="發票抬頭" value={order.InvoiceTitle} />
                    <InfoTile label="載具號碼" value={order.InvoiceCarrier} />
                    <InfoTile label="捐贈碼" value={order.InvoiceDonationCode} />
                    <div className="md:col-span-2">
                      <InfoTile label="發票備註" value={order.InvoiceNote} />
                    </div>
                  </div>
                  <DeliveryChecklistSummary order={order} />
                  <div className="rounded-md border border-[#dfe6e1] bg-white p-4">
                    <div className="flex flex-col gap-3 2xl:flex-row 2xl:items-start 2xl:justify-between">
                      <div className="min-w-0">
                        <div className="mb-2 flex flex-wrap items-center gap-2">
                          <Mail size={18} className="text-[#315746]" />
                          <h3 className="font-extrabold text-[#203d32]">通知紀錄中心</h3>
                          {pickupNotification && <span className="rounded-full bg-[#e9f4ec] px-2.5 py-1 text-xs font-bold text-[#245438]">Email 已通知 {pickupNotificationCount} 次</span>}
                        </div>
                        <p className="text-sm font-medium leading-6 text-[#59665f]">
                          商品到店後可寄送 Email，也可記錄電話、LINE、簡訊或其他聯絡結果，方便後續追蹤未取件。
                        </p>
                        {!order.CustomerEmail && <p className="mt-2 text-xs font-bold text-[#9a5b15]">此訂單沒有 Email，請先編輯訂單補上客戶信箱；仍可新增電話或 LINE 通知紀錄。</p>}
                        {order.CustomerEmail && !orderCanSendPickupNotification && <p className="mt-2 text-xs font-bold text-[#7b877f]">需等流程更新為「已出庫到店」或「可通知取件」後才能寄送取件 Email。</p>}
                      </div>
                      <div className="rwd-action-grid w-full 2xl:min-w-[320px]">
                        <button
                          className="inline-flex h-11 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-[#315746] px-5 font-bold text-white disabled:bg-[#dfe6e1] disabled:text-[#8d9b93]"
                          disabled={sendingNotification || !orderCanSendPickupNotification || !order.CustomerEmail}
                          onClick={handleSendPickupNotification}
                          type="button"
                        >
                          <Mail size={18} />{sendingNotification ? "寄送中" : pickupNotification ? "再次寄送 Email" : "寄送 Email"}
                        </button>
                        <button
                          className="inline-flex h-11 items-center justify-center gap-2 whitespace-nowrap rounded-md border border-[#cfd8d2] bg-white px-5 font-bold text-[#315746] hover:bg-[#f0f5f2]"
                          onClick={() => setNotificationLogOpen((value) => !value)}
                          type="button"
                        >
                          <MessageSquarePlus size={18} />新增紀錄
                        </button>
                      </div>
                    </div>

                    {notificationLogOpen && (
                      <div className="mt-4 rounded-md border border-[#edf0ee] bg-[#fbfcfb] p-3">
                        <div className="grid gap-3 md:grid-cols-[minmax(140px,180px)_minmax(140px,180px)_1fr]">
                          <Select label="通知方式" options={notificationMethods} value={notificationMethod} onChange={setNotificationMethod} />
                          <Select label="結果" options={notificationStatuses} value={notificationStatus} onChange={setNotificationStatus} />
                          <Input label="紀錄內容" placeholder="例如：已電話通知，客戶預計週五下午取件。" value={notificationBody} onChange={setNotificationBody} />
                        </div>
                        <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:justify-end">
                          <button className="inline-flex h-10 items-center justify-center rounded-md border border-[#cfd8d2] bg-white px-4 text-sm font-bold text-[#315746]" onClick={() => setNotificationLogOpen(false)} type="button">取消</button>
                          <button className="inline-flex h-10 items-center justify-center gap-2 rounded-md bg-[#315746] px-4 text-sm font-bold text-white disabled:opacity-60" disabled={saving || notificationBody.trim() === ""} onClick={handleAddNotificationLog} type="button"><Save size={16} />儲存紀錄</button>
                        </div>
                      </div>
                    )}

                    <div className="mt-4 grid gap-2">
                      {notificationHistory.length === 0 ? (
                        <EmptyText text="尚無通知紀錄。" />
                      ) : (
                        notificationHistory.slice(0, 6).map((item) => (
                          <div className="grid gap-2 rounded-md border border-[#edf0ee] bg-[#fbfcfb] p-3 text-sm md:grid-cols-[110px_110px_minmax(0,1fr)_170px]" key={item.StoreSalesOrderNotificationId}>
                            <div className="font-extrabold text-[#203d32]">{notificationMethodLabel(item.Method)}</div>
                            <div className="font-bold text-[#59665f]">{notificationStatusLabel(item.Status)}</div>
                            <div className="min-w-0">
                              <p className="break-words font-medium leading-6 text-[#59665f]">{item.BodySummary || item.Subject || "-"}</p>
                              <p className="mt-1 break-all text-xs font-bold text-[#7b877f]">{item.RecipientEmail}</p>
                            </div>
                            <div className="text-left text-xs font-bold text-[#7b877f] md:text-right">
                              <p>{item.SentAt || item.CreatedAt || "-"}</p>
                              <p className="mt-1">{item.StoreUserName || item.AdminUserName || "系統人員"}</p>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                  <div className="rounded-md border border-[#dfe6e1] bg-white p-4">
                    <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                      <div className="min-w-0">
                        <h3 className="font-extrabold text-[#203d32]">文件列印</h3>
                        <p className="mt-1 text-sm font-medium leading-6 text-[#7b877f]">訂購確認單給客戶確認訂購內容；收款收據請於下方每筆流水補印；交付確認單需商品完成交付後使用。</p>
                      </div>
                      <div className="rwd-action-grid">
                        <button className="inline-flex h-11 min-w-[132px] shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-[#315746] px-4 font-bold text-white" onClick={() => printDocument(`訂購確認單-${order.OrderNumber}`, orderPrintHtml(order))} type="button"><Printer size={18} />訂購確認單</button>
                        <button
                          className="inline-flex h-11 min-w-[132px] shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746] disabled:bg-[#f4f6f5] disabled:text-[#9aa8a0]"
                          disabled={!orderDeliveryPrintable}
                          onClick={() => printDocument(`交付確認單-${order.OrderNumber}`, deliveryPrintHtml(order))}
                          title={orderDeliveryPrintable ? "列印交付確認單" : "商品完成交付後才可列印"}
                          type="button"
                        >
                          <ClipboardCheck size={18} />交付確認單
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="rounded-md border border-[#dfe6e1] bg-white p-4">
                    <div className="mb-3 flex items-center gap-2">
                      <ReceiptText size={18} className="text-[#315746]" />
                      <h3 className="font-extrabold text-[#203d32]">收款紀錄</h3>
                    </div>
                    {(order.Payments || []).length === 0 ? (
                      <EmptyText text="尚無收款流水。" />
                    ) : (
                      <div className="grid gap-2">
                        {(order.Payments || []).map((item) => (
                          <div className="grid gap-2 rounded-md border border-[#edf0ee] bg-[#fbfcfb] p-3 text-sm md:grid-cols-[120px_1fr_150px_auto] md:items-center" key={item.StoreSalesOrderPaymentId}>
                            <div className="font-extrabold text-[#203d32]">{item.PaymentTypeLabel || item.PaymentType}</div>
                            <div className="min-w-0 text-[#59665f]">
                              <p className="font-bold">{item.PaymentMethodLabel || item.PaymentMethod} / {item.StoreUserName || "未記錄"}</p>
                              {item.Note && <p className="mt-1 break-words text-xs">{item.Note}</p>}
                            </div>
                            <div className="text-left md:text-right">
                              <p className="font-extrabold text-[#203d32]">{money(item.Amount)}</p>
                              <p className="text-xs font-bold text-[#7b877f]">{item.ReceivedAt || "-"}</p>
                            </div>
                            <button className="inline-flex h-10 min-w-[112px] shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md border border-[#cfd8d2] bg-white px-3 text-sm font-bold text-[#315746] hover:bg-[#f0f5f2]" onClick={() => printDocument(`收款收據-${order.OrderNumber}-${item.StoreSalesOrderPaymentId}`, receiptPrintHtml(order, item))} type="button"><Printer size={16} />列印收據</button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {order.Status === "Voided" && <div className="rounded-md border border-[#f2c9c6] bg-[#fff8f7] px-4 py-3 text-sm font-bold text-[#9a2d2d]">作廢原因：{order.VoidReason || "-"}</div>}
                  <div className="overflow-x-auto rounded-md border border-[#edf0ee]">
                    <table className="min-w-[980px] w-full text-left text-sm">
                      <thead className="bg-[#fbfcfb] text-xs font-bold text-[#7b877f]">
                        <tr><th className="p-3">商品</th><th className="p-3">SKU</th><th className="p-3">序號</th><th className="p-3">耳側</th><th className="p-3">保固起算</th><th className="p-3">成交價</th><th className="p-3">數量</th><th className="p-3 text-right">小計</th></tr>
                      </thead>
                      <tbody>
                        {(order.Items || []).map((item) => (
                          <tr className="border-t border-[#edf0ee]" key={item.StoreSalesOrderItemId}>
                            <td className="p-3 font-extrabold text-[#203d32]">{item.ProductName}{item.VariantLabel ? ` / ${item.VariantLabel}` : ""}</td>
                            <td className="p-3 font-bold text-[#59665f]">{item.Sku || "-"}</td>
                            <td className="p-3">{item.SerialNumber || "-"}</td>
                            <td className="p-3">{earSideLabel(item.EarSide)}</td>
                            <td className="p-3">{item.WarrantyStartDate || "-"}</td>
                            <td className="p-3">{money(item.DealPrice)}</td>
                            <td className="p-3">{item.Quantity}</td>
                            <td className="p-3 text-right font-extrabold text-[#203d32]">{money(item.LineTotal)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="grid gap-3 lg:grid-cols-2">
                    <TextBlock title="客戶備註" text={order.CustomerNote} />
                    <TextBlock title="內部備註" text={order.InternalNote} />
                  </div>
                </div>
              ) : (
                <EmptyText text="找不到門市訂單。" />
              )}
            </section>
          </section>
        </div>
      </div>
    </StoreLayout>
  );
}

function Input({ label, onChange, placeholder = "", type = "text", value }: { label: string; onChange: (value: string) => void; placeholder?: string; type?: string; value: string }) {
  return (
    <div className="min-w-0">
      <label className="mb-1 block text-sm font-bold text-[#59665f]">{label}</label>
      <input className="h-11 w-full rounded-md border border-[#cfd8d2] px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" placeholder={placeholder} type={type} value={value} onChange={(event) => onChange(event.target.value)} />
    </div>
  );
}

function NumberInput({ compact = false, fluid = false, onChange, value }: { compact?: boolean; fluid?: boolean; onChange: (value: number) => void; value: number }) {
  return <input className={`${fluid ? "w-full" : compact ? "w-20" : "w-28"} h-10 rounded-md border border-[#cfd8d2] px-2 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15`} min={compact ? 1 : 0} type="number" value={value} onChange={(event) => onChange(Number(event.target.value || 0))} />;
}

function SmallInput({ fluid = false, onChange, placeholder = "", type = "text", value }: { fluid?: boolean; onChange: (value: string) => void; placeholder?: string; type?: string; value: string }) {
  return <input className={`${fluid ? "w-full" : "w-36"} h-10 rounded-md border border-[#cfd8d2] px-2 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15`} placeholder={placeholder} type={type} value={value} onChange={(event) => onChange(event.target.value)} />;
}

function CompactField({ children, label }: { children: ReactNode; label: string }) {
  return <label className="grid gap-1 text-xs font-bold text-[#7b877f]"><span>{label}</span>{children}</label>;
}

function Select({ label, onChange, options, value }: { label: string; onChange: (value: string) => void; options: { Key: string; Label: string }[]; value: string }) {
  return (
    <div className="min-w-0">
      <label className="mb-1 block text-sm font-bold text-[#59665f]">{label}</label>
      <select className="h-11 w-full rounded-md border border-[#cfd8d2] bg-white px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={value} onChange={(event) => onChange(event.target.value)}>
        {options.map((item) => <option key={item.Key || "all"} value={item.Key}>{item.Label}</option>)}
      </select>
    </div>
  );
}

function DeliveryCompletedField({ checked, disabled, onChange }: { checked: boolean; disabled: boolean; onChange: (checked: boolean) => void }) {
  return (
    <div>
      <label className="mb-1 block text-sm font-bold text-[#59665f]">現場交付</label>
      <label
        className={`flex h-11 w-full items-center gap-2 rounded-md border px-3 text-sm font-bold transition ${
          disabled
            ? "border-[#dfe6e1] bg-[#f4f6f5] text-[#9aa8a0]"
            : "border-[#cfd8d2] bg-white text-[#315746] hover:border-[#9ab5a6]"
        }`}
      >
        <input
          checked={checked}
          className="h-4 w-4 accent-[#315746]"
          disabled={disabled}
          type="checkbox"
          onChange={(event) => onChange(event.target.checked)}
        />
        <span>已現場交付</span>
      </label>
    </div>
  );
}

function TextArea({ label, onChange, placeholder, value }: { label: string; onChange: (value: string) => void; placeholder: string; value: string }) {
  return (
    <div>
      <label className="mb-1 block text-sm font-bold text-[#59665f]">{label}</label>
      <textarea className="min-h-[112px] w-full rounded-md border border-[#cfd8d2] p-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" placeholder={placeholder} value={value} onChange={(event) => onChange(event.target.value)} />
    </div>
  );
}

function TextBlock({ text, title }: { text?: string | null; title: string }) {
  return (
    <section className="rounded-md border border-[#dfe6e1] bg-[#fbfcfb] p-4">
      <h3 className="mb-3 font-extrabold text-[#203d32]">{title}</h3>
      <p className="whitespace-pre-wrap text-sm leading-6 text-[#59665f]">{text || "-"}</p>
    </section>
  );
}

function EmptyText({ text }: { text: string }) {
  return <div className="rounded-md border border-dashed border-[#cfd8d2] bg-[#f7faf8] px-4 py-8 text-center text-sm text-[#7b877f]">{text}</div>;
}
